import { createContext, useContext, useState, useCallback, useEffect, ReactNode, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type Language = 'fr' | 'en' | 'lg-fr' | 'sw';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, fallback?: string) => string;
  reloadTranslations: () => Promise<void>;
  translationStats: {
    codeKeys: number;
    dbKeys: number;
    totalKeys: number;
  };
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Cache pour les traductions de la base de données
interface TranslationCache {
  [key: string]: {
    [language: string]: string;
  };
}

const translationCache: TranslationCache = {};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Basic translations for core functionality - rest will be in database
const basicTranslations = {
  fr: {
    // Basic navigation and common terms
    'home': 'Accueil',
    'products': 'Produits',
    'categories': 'Catégories',
    'login': 'Connexion',
    'logout': 'Déconnexion',
    'register': 'Inscription',
    'dashboard': 'Tableau de bord',
    'admin': 'Administration',
    'settings': 'Paramètres',
    'profile': 'Profil',
    'orders': 'Commandes',
    'cart': 'Panier',
    'search': 'Rechercher',
    'loading': 'Chargement...',
    'error': 'Erreur',
    'success': 'Succès',
    'cancel': 'Annuler',
    'confirm': 'Confirmer',
    'save': 'Enregistrer',
    'edit': 'Modifier',
    'delete': 'Supprimer',
    'add': 'Ajouter',
    'create': 'Créer',
    'update': 'Mettre à jour',
    'view': 'Voir',
    'close': 'Fermer',
    'open': 'Ouvrir',
    'yes': 'Oui',
    'no': 'Non',
    'next': 'Suivant',
    'previous': 'Précédent',
    'continue': 'Continuer',
    'back': 'Retour',
    'submit': 'Soumettre',
    'reset': 'Réinitialiser',
    'filter': 'Filtrer',
    'sort': 'Trier',
    'name': 'Nom',
    'email': 'Email',
    'phone': 'Téléphone',
    'address': 'Adresse',
    'city': 'Ville',
    'country': 'Pays',
    'date': 'Date',
    'time': 'Heure',
    'status': 'Statut',
    'active': 'Actif',
    'inactive': 'Inactif',
    'pending': 'En attente',
    'approved': 'Approuvé',
    'rejected': 'Rejeté',
    'completed': 'Terminé',
    'cancelled': 'Annulé',
    'total': 'Total',
    'price': 'Prix',
    'quantity': 'Quantité',
    'description': 'Description',
    'category': 'Catégorie',
    'language': 'Langue',
    'currency': 'Devise',

    // Header
    'header.hello': 'Bonjour',
    'header.menu': 'Menu',
    'header.home': 'Accueil',
    'header.all_products': 'Tous les produits',
    'header.your_orders': 'Vos commandes',
    'header.your_account': 'Votre compte',
    'header.vendor_account': 'Compte vendeur',
    'header.admin_panel': 'Administration',
    'header.points': 'Points Relais',
    'header.become_vendor': 'Devenir vendeur',
    'header.relay_management': 'Gestion des relais',
    'header.create_business': 'Créer mon business',
    'header.cart': 'Panier',
    'header.logout': 'Déconnexion',
    'header.login': 'Connexion',
    'header.search.placeholder': 'Rechercher un produit...',
    'header.deals': 'Promotions',
    'header.buyer_account': 'Compte acheteur',
    'header.my_profile': 'Mon profil',
    'header.my_orders': 'Mes commandes',
    'header.vendor_dashboard': 'Espace vendeur',
    'header.relay_dashboard': 'Point Relais',
    'header.admin_account': 'Compte administrateur',
    'header.administration': 'Administration',

    // Site texts
    'site.tagline': 'Votre marketplace locale',
    'site.description': 'Découvrez les meilleurs produits locaux et internationaux directement à Kinshasa',

    // Admin
    'admin.title': 'Administration',
    'admin.subtitle': 'Tableau de bord administrateur',
    'admin.system_operational': 'Système opérationnel',
    'admin.tabs.dashboard': 'Tableau de bord',
    'admin.tabs.users': 'Utilisateurs',
    'admin.tabs.vendors': 'Vendeurs',
    'admin.tabs.employees': 'Employés',
    'admin.tabs.relays': 'Points relais',
    'admin.tabs.orders': 'Commandes',
    'admin.tabs.cms': 'CMS',
    'admin.tabs.config': 'Configuration',
    'admin.tabs.roles': 'Rôles',
  'admin.translation_checker': 'Vérificateur de traductions',
  'admin.translation_checker_description': 'Détecte automatiquement les textes en dur non traduits dans votre code',
  'admin.scanning': 'Analyse en cours...',
  'admin.start_scan': 'Lancer l\'analyse',
  'admin.scan_complete': 'Analyse terminée',
  'admin.issues_found': 'problèmes trouvés',
  'admin.scan_error': 'Erreur lors de l\'analyse',
  'admin.files_scanned': 'Fichiers analysés',
  'admin.files_with_issues': 'Fichiers avec problèmes',
  'admin.files_ok': 'Fichiers OK',
  'admin.affected_files': 'Fichiers concernés',
  'admin.issues_in_file': 'Problèmes dans ce fichier',
  'admin.all_issues': 'Tous les problèmes',
  'admin.quick_actions': 'Actions rapides',
  'admin.new_category': 'Nouvelle catégorie',
  'admin.new_page': 'Nouvelle page',
  'admin.new_footer_section': 'Nouvelle section footer',
  'admin.cms.loading': 'Chargement...',
  'admin.cms.editor': 'Éditeur',
  'admin.cms.preview': 'Prévisualiser',
  'admin.cms.save': 'Sauvegarder',
  'admin.cms.hero_tab': 'Hero',
  'admin.cms.sections_tab': 'Sections',
  'admin.cms.faq_tab': 'FAQ',
  'admin.cms.hero_title': 'Titre Hero',
  'admin.cms.hero_description': 'Description Hero',
  'admin.cms.hero_title_placeholder': 'Titre principal de la page',
  'admin.cms.hero_description_placeholder': 'Description sous le titre',
  'admin.cms.section_title': 'Titre de la section',
  'admin.cms.section_content': 'Contenu',
  'admin.cms.section_title_placeholder': 'Titre de la section',
  'admin.cms.section_content_placeholder': 'Contenu de la section',
  'admin.cms.faq_title': 'Questions Fréquentes',
  'admin.cms.add_faq': 'Ajouter une question',
  'admin.cms.question': 'Question',
  'admin.cms.answer': 'Réponse',
  'admin.cms.question_placeholder': 'Entrez la question',
  'admin.cms.answer_placeholder': 'Entrez la réponse',
  'admin.cms.saved': 'Contenu sauvegardé',
  'admin.cms.saved_description': 'Les modifications ont été enregistrées avec succès',
  'admin.cms.error': 'Erreur',
  'admin.cms.no_faq': 'Aucune question ajoutée. Cliquez sur "Ajouter une question" pour commencer.',

  // CMS Configuration
  'admin.config.title': 'Configuration du site',
  'admin.config.subtitle': 'Interface complète pour personnaliser votre marketplace',
  'admin.config.site_content': 'Contenu du site',
  'admin.config.test_mode': 'Mode Test Stock',
  'admin.config.system_settings': 'Paramètres système',
  'admin.config.dashboard': 'Tableau de bord',
  'admin.config.hero': 'Hero',
  'admin.config.categories': 'Catégories',
  'admin.config.pages': 'Pages',
  'admin.config.menus': 'Menus',
  'admin.config.footer': 'Footer',
  'admin.config.active_categories': 'Catégories actives',
  'admin.config.total_categories': 'Total des catégories',
  'admin.config.category_management': 'Gestion des catégories',
  'admin.config.category_management_desc': 'Personnalisez les catégories de produits affichées sur votre marketplace',
  'admin.config.category_active': 'Actif',
  'admin.config.category_inactive': 'Inactif',
  'admin.config.products': 'produit(s)',
  'admin.config.edit_category': 'Modifier la catégorie',
  'admin.config.delete_category': 'Supprimer la catégorie',
  'admin.config.delete_confirm': 'Êtes-vous sûr de vouloir supprimer cette catégorie ?',
  'admin.config.category_deleted': 'Catégorie supprimée avec succès',

    // About Page
    'about.hero_title': 'À Propos',
    'about.hero_description': 'Votre marketplace de confiance pour des achats locaux et sécurisés',
    'about.mission_title': 'Notre Mission',
    'about.values_title': 'Nos Valeurs',
    'about.security': 'Sécurité',
    'about.security_description': 'Transactions sécurisées et protection des données personnelles',
    'about.community': 'Communauté',
    'about.community_description': 'Création d\'une communauté solidaire et engagée',
    'about.reliability': 'Fiabilité',
    'about.reliability_description': 'Service de qualité avec un réseau de points relais certifiés',
    'about.innovation': 'Innovation',
    'about.innovation_description': 'Technologies modernes pour une expérience utilisateur optimale',
    'about.active_vendors': 'Vendeurs Actifs',
    'about.satisfied_customers': 'Clients Satisfaits',
    'about.relay_points': 'Points Relais',

    // How It Works Page
    'howItWorks.hero_title': 'Comment Ça Marche',
    'howItWorks.hero_description': 'Découvrez comment utiliser notre plateforme en toute simplicité',
    'howItWorks.buyers_title': 'Pour les Acheteurs',
    'howItWorks.sellers_title': 'Pour les Vendeurs',
    'howItWorks.advantages_title': 'Nos Avantages',
    'howItWorks.step1_title': 'Recherchez un produit',
    'howItWorks.step1_description': 'Parcourez notre catalogue et trouvez les produits qui vous intéressent',
    'howItWorks.step2_title': 'Choisissez un point relais',
    'howItWorks.step2_description': 'Sélectionnez le point de retrait le plus proche de chez vous',
    'howItWorks.step3_title': 'Payez en ligne',
    'howItWorks.step3_description': 'Effectuez votre paiement en toute sécurité via notre plateforme',
    'howItWorks.step4_title': 'Retirez votre commande',
    'howItWorks.step4_description': 'Présentez-vous au point relais avec votre code QR pour récupérer vos produits',
    'howItWorks.seller_step1_title': 'Créez votre boutique',
    'howItWorks.seller_step1_description': 'Inscrivez-vous et créez votre espace vendeur en quelques clics',
    'howItWorks.seller_step2_title': 'Ajoutez vos produits',
    'howItWorks.seller_step2_description': 'Publiez vos articles avec photos et descriptions détaillées',
    'howItWorks.seller_step3_title': 'Livrez au point relais',
    'howItWorks.seller_step3_description': 'Déposez les commandes au point relais le plus proche',
    'howItWorks.advantage1_title': 'Sécurité maximale',
    'howItWorks.advantage1_description': 'Vos transactions sont protégées et vos données sécurisées',
    'howItWorks.advantage2_title': 'Flexibilité de paiement',
    'howItWorks.advantage2_description': 'Plusieurs options de paiement disponibles',
    'howItWorks.advantage3_title': 'Support local',
    'howItWorks.advantage3_description': 'Soutenez l\'économie locale en achetant près de chez vous',

    // Support Page
    'support.hero_title': 'Support Client',
    'support.hero_description': 'Nous sommes là pour vous aider',
    'support.contact_title': 'Contactez-nous',
    'support.contact_description': 'Plusieurs moyens de nous joindre',
    'support.email': 'Email',
    'support.phone': 'Téléphone',
    'support.live_chat': 'Chat en direct',
    'support.form_title': 'Envoyez-nous un message',
    'support.form_description': 'Remplissez le formulaire ci-dessous',
    'support.faq_title': 'Questions Fréquentes',
    'support.faq_description': 'Trouvez rapidement des réponses',
    'support.hours_title': 'Heures d\'ouverture',
    'support.hours_description': 'Nous sommes disponibles',
    'support.weekdays': 'Lundi - Vendredi',
    'support.weekend': 'Samedi - Dimanche',
    'support.form_name': 'Nom complet',
    'support.form_email': 'Email',
    'support.form_subject': 'Sujet',
    'support.form_message': 'Message',
    'support.form_submit': 'Envoyer',
    'support.form_sending': 'Envoi...',
    'support.form_success': 'Message envoyé avec succès',
    'support.form_success_description': 'Nous vous répondrons dans les plus brefs délais',
    'support.form_error': 'Erreur d\'envoi',
    'support.name_label': 'Nom complet',
    'support.name_placeholder': 'Votre nom et prénom',
    'support.email_label': 'Adresse email',
    'support.email_placeholder': 'votre.email@exemple.com',
    'support.subject_label': 'Sujet',
    'support.subject_placeholder': 'Objet de votre message',
    'support.message_label': 'Message',
    'support.message_placeholder': 'Décrivez votre demande en détail...',
    'support.send': 'Envoyer le message',
    'support.success_message': 'Message envoyé avec succès ! Nous vous répondrons rapidement.',
    'support.error_message': 'Erreur lors de l\'envoi du message. Veuillez réessayer.',
    
    // Relay Points Page
    'relayPoints.hero_title': 'Points Relais',
    'relayPoints.hero_description': 'Trouvez le point relais le plus proche',
    'relayPoints.search_placeholder': 'Rechercher par nom, adresse ou commune...',
    'relayPoints.how_it_works_title': 'Comment ça marche ?',
    'relayPoints.how_it_works_description': 'Retirer vos commandes en 4 étapes simples',
    'relayPoints.no_results': 'Aucun point relais trouvé',
    'relayPoints.loading': 'Chargement des points relais...',
    'relayPoints.localize': 'Localiser',
    'relayPoints.itinerary': 'Itinéraire',
    'relayPoints.contact': 'Contacter',
    'relayPoints.choose': 'Choisir',
    'relayPoints.selected': 'Point relais sélectionné',
    'admin.tabs.activity': 'Activité',
    'admin.tabs.bulk': 'Actions groupées',
    'admin.analytics.users': 'Utilisateurs',
    'admin.analytics.vendors': 'Vendeurs',
    'admin.analytics.orders': 'Commandes',
    'admin.analytics.revenue': 'Revenus',
    'admin.analytics.employees': 'Employés',

    // CMS Tabs
    'cms.tabs.dashboard': 'Tableau de bord',
    'cms.tabs.settings': 'Paramètres',
    'cms.tabs.categories': 'Catégories',
    'cms.tabs.menus': 'Menus',
    'cms.tabs.footer': 'Pied de page',
    'cms.tabs.pages': 'Pages',

    // CMS Site Configuration
    'cms.site_configuration_title': 'Configuration du site',
    'cms.site_configuration_subtitle': 'Interface complète pour personnaliser votre marketplace',
    'cms.tabs.site_content': 'Contenu du site',
    'cms.tabs.system_settings': 'Paramètres système',

    // CMS Footer Admin
    'cms.footer.sections_title': 'Sections du pied de page',
    'cms.footer.sections_desc': 'Personnalisez les sections affichées dans le footer de votre site',
    'cms.footer.new_section': 'Nouvelle section',
    'cms.footer.no_sections': 'Aucune section footer',
    'cms.footer.create_first_section': 'Commencez par créer votre première section de pied de page',
    'cms.footer.edit_section': 'Modifier la section footer',
    'cms.footer.dialog_desc': 'Configurez les sections et liens du pied de page',
    'cms.footer.cancel': 'Annuler',
    'cms.footer.save_edit': 'Modifier',
    'cms.footer.save_create': 'Créer',
    'cms.footer.links': 'liens',
    'cms.footer.more': 'autre(s) lien(s)',
    'cms.footer.untitled': 'Sans titre',
    'cms.footer.confirm_delete': 'Êtes-vous sûr de vouloir supprimer cette section ?',
     'cms.footer.updated_success': 'Section footer mise à jour avec succès',
     'cms.footer.created_success': 'Section footer créée avec succès',
     'cms.footer.deleted_success': 'Section footer supprimée avec succès',
     'cms.footer.section_title': 'Titre de la section',
     'cms.footer.section_key': 'Clé de la section',
     'cms.footer.section_links': 'Liens de la section',
     'cms.footer.section_active': 'Section active',
     'cms.footer.title_placeholder': 'À propos',
     'cms.footer.key_placeholder': 'about',
     'cms.footer.link_title': 'Titre',
     'cms.footer.link_url': 'URL',
     'cms.footer.link_icon': 'Icône (optionnel)',
     'cms.footer.link_title_placeholder': 'Notre histoire',
     'cms.footer.link_url_placeholder': '/about',
     'cms.footer.add_link': 'Ajouter un lien',
     'cms.footer.remove_link': 'Supprimer le lien',
     'cms.footer.delete_section': 'Supprimer la section',
     'cms.footer.sort_order': 'Ordre d\'affichage',

    // User Dashboard
    'dashboard.title': 'Tableau de bord',
    'dashboard.welcome': 'Bienvenue',
    'dashboard.logout': 'Déconnexion',
    'dashboard.total_orders': 'Total des commandes',
    'dashboard.pending_orders': 'Commandes en attente',
    'dashboard.completed_orders': 'Commandes terminées',
    'dashboard.my_orders_tab': 'Mes commandes',
    'dashboard.payments_tab': 'Paiements',
    'dashboard.disputes_tab': 'Réclamations',
    'dashboard.messages_tab': 'Messages',
    'dashboard.account_settings_tab': 'Paramètres du compte',
    'dashboard.my_orders_title': 'Mes commandes',
    'dashboard.my_orders_desc': 'Suivez vos commandes et leur statut',
    'dashboard.no_orders': "Vous n'avez pas encore de commandes",
    'dashboard.discover_products': 'Découvrir les produits',
    'dashboard.pickupCode': 'Code de retrait',
    'dashboard.presentCode': 'Présentez ce code au point relais pour retirer votre commande',
    'dashboard.expiresOn': 'Expire le',
    'dashboard.viewQRCode': 'Voir le code QR',
    'dashboard.defaultRelay': 'Point relais par défaut',
    'dashboard.selectRelayPreference': 'Sélectionnez votre point relais préféré pour vos futures commandes',
    'dashboard.relayPoint': 'Point Relais',
    'dashboard.register': 'Enregistrer',
    'dashboard.selectRelay': 'Sélectionner un point relais',
    'dashboard.saving': 'Enregistrement...',
    'dashboard.selectRelayError': 'Veuillez sélectionner un point relais',
    'dashboard.relayUpdated': 'Point relais par défaut enregistré',
    'dashboard.updateError': 'Erreur lors de la mise à jour',

    // Order Status
    'order.status.pending_payment': 'En attente de paiement',
    'order.status.paid': 'Payé',
    'order.status.awaiting_drop_off': 'En attente de dépôt',
    'order.status.available_for_pickup': 'Prêt pour retrait',
    'order.status.picked_up': 'Retiré',
    'order.status.expired': 'Expiré',
    'order.status.dispute': 'Litige',
    'order.status.cancelled': 'Annulée',

    'admin.employees.title': 'Gestion des Utilisateurs',
    'admin.employees.subtitle': 'Gérez les comptes utilisateurs et leurs permissions',
    'admin.users.export': 'Exporter',
    'admin.users.new_user': 'Nouvel utilisateur',
    'admin.config.categories.general': 'Général',
    'admin.config.categories.general_desc': 'Paramètres généraux du site',
    'admin.config.site_info': 'Informations du site',
    'admin.config.site_name': 'Nom du site',
    'admin.config.slogan': 'Slogan',
    'admin.config.slogan_desc': 'Description courte de votre plateforme',
    'admin.config.description': 'Description',
    'admin.config.description_desc': 'Description détaillée pour le SEO',
    'admin.config.reset': 'Réinitialiser',
    'admin.config.save_all': 'Sauvegarder tout',
    'admin.config.advanced': 'Configuration avancée',
    'admin.config.advanced_desc': 'Personnalisez tous les aspects de votre plateforme',
    'admin.config.appearance': 'Apparence',
    'admin.config.appearance_desc': 'Personnalisation visuelle',
    'admin.config.payments': 'Paiements',
    'admin.config.payments_desc': 'Configuration des paiements',
    'admin.config.delivery': 'Livraison',
    'admin.config.delivery_desc': 'Paramètres de livraison',
    'admin.config.notifications': 'Notifications',
    'admin.config.notifications_desc': 'Gestion des notifications',

    // HR Management
    'hr.total_employees': 'Total employés',
    'hr.active': 'Actifs',
    'hr.positions': 'Positions',
    'hr.departments': 'Départements',
    'hr.employees': 'Employés',
    'hr.leave': 'Congés',
    'hr.evaluations': 'Évaluations',
    'hr.training': 'Formations',
    'hr.documents': 'Documents',
    'hr.employee_management': 'Gestion des employés',
    'hr.employee_management_desc': 'Administrez les profils et informations des employés',
    'hr.new_employee': 'Nouvel employé',
    'hr.employee': 'Employé',
    'hr.code': 'Code',
    'hr.position': 'Position',
    'hr.department': 'Département',
    'hr.status': 'Statut',
    'hr.actions': 'Actions',
    'hr.modify': 'Modifier',
    'employee': 'Employé',
    'code': 'Code',
    'position': 'Poste',
    'department': 'Département',
    'account': 'Compte',
    'employees.status': 'Statut',
    'actions': 'Actions',
    'employees.active': 'Actif',
    'employees.inactive': 'Inactif',
    'terminated': 'Terminé',
    'filtered': 'filtré(s)',
    'employees.total': 'au total',
    'no_employees_found': 'Aucun employé trouvé',
    'create_account': 'Créer compte',
    'create_user_account': 'Créer un compte utilisateur',
    'link_user_account': 'Lier un compte utilisateur',
    'link_account': 'Lier le compte',
    'select_user': 'Sélectionner un utilisateur',
    'select_user_placeholder': 'Choisir un utilisateur...',
    'no_available_users': 'Aucun utilisateur disponible pour la liaison',
    'system_role': 'Rôle système',
    'temporary_password': 'Mot de passe temporaire',
    'enter_password': 'Saisir le mot de passe',
    'password_help': 'L\'employé devra changer ce mot de passe à la première connexion',
    'fill_required_fields': 'Veuillez remplir tous les champs obligatoires',
    'employees.success': 'Succès',

    // API Keys
    'admin.api_keys.tab': 'Clés API',
    'admin.api_keys.title': 'Gestion des clés API',
    'admin.api_keys.description': 'Gérez les clés d\'accès aux API externes',

    // Relay Points
    'relay.points': 'Points relais',
    'relay.manage_network': 'Gérez le réseau de points de retrait',
    'relay.new_point': 'Nouveau point relais',
    'relay.view_map': 'Voir la carte',
    'relay.point': 'Point relais',
    'relay.contact': 'Contact',
    'relay.address': 'Adresse',
    'relay.status': 'Statut',
    'relay.actions': 'Actions',
    'relay.modify': 'Modifier',

    // Home - Relay Section
    'relay.title': 'Points relais près de chez vous',
    'relay.description': 'Trouvez un point relais pour récupérer vos commandes facilement.',
    'relay.step1': 'Recherchez',
    'relay.step1.desc': 'Entrez votre quartier ou commune',
    'relay.step2': 'Choisissez',
    'relay.step2.desc': 'Sélectionnez le point le plus proche',
    'relay.step3': 'Validez',
    'relay.step3.desc': 'Confirmez lors du paiement',
    'relay.step4': 'Récupérez',
    'relay.step4.desc': 'Présentez votre code de retrait',
    'relay.find_point': 'Trouver un point relais',

    // Orders
    'orders.back_home': 'Retour à l\'accueil',
    'orders.title': 'Mes commandes',
    'orders.subtitle': 'Suivez l\'état de toutes vos commandes',
    'orders.tabs.all': 'Toutes',
    'orders.tabs.active': 'En cours',
    'orders.tabs.completed': 'Terminées',
    'orders.tabs.cancelled': 'Annulées',
    'orders.loading': 'Chargement...',
    'orders.order_number': 'Commande',
    'orders.placed_on': 'Passée le',
    'orders.view_details': 'Voir les détails',
    'orders.pay_now': 'Payer maintenant',
    'orders.buy_again': 'Racheter',
    'orders.paid': 'Payé',
    'orders.preparing': 'En préparation',
    'orders.ready_for_pickup': 'Prêt pour retrait',
    'orders.no_orders': 'Aucune commande',
    'orders.no_orders_message': 'Vous n\'avez pas encore passé de commande.',
    'orders.start_shopping': 'Commencer mes achats',
    'orders.filters.title': 'Filtres, recherche et tri',
    'orders.filters.reset': 'Réinitialiser',
    'orders.filters.status': 'Statut',
    'orders.filters.all_statuses': 'Tous les statuts',
    'orders.filters.all': 'Tous les statuts',
    'orders.filters.search': 'Recherche',
    'orders.filters.search_placeholder': 'N° commande, client, vendeur...',
    'orders.filters.vendor_search_placeholder': 'N° commande, produit, point relais...',
    'orders.filters.period': 'Période',
    'orders.filters.all_periods': 'Toutes les périodes',
    'orders.filters.today': 'Aujourd\'hui',
    'orders.filters.week': 'Cette semaine',
    'orders.filters.month': 'Ce mois',
    'orders.filters.year': 'Cette année',
    'orders.filters.sort_by': 'Trier par',
    'orders.filters.sort_date': 'Date de création',
    'orders.filters.sort_amount': 'Montant',
    'orders.filters.sort_status': 'Statut',
    'orders.filters.sort_number': 'N° commande',
    'orders.status.PENDING_PAYMENT': 'En attente paiement',
    'orders.status.PAID': 'Payé',
    'orders.status.PREPARING': 'En préparation',
    'orders.status.READY_FOR_PICKUP': 'Prêt pour retrait',
    'orders.status.PICKED_UP': 'Récupéré',
    'orders.status.CANCELLED': 'Annulé',
    'orders.status.EXPIRED': 'Expiré',

    // Employee management
    'employee.management': 'Gestion des utilisateurs',
    'employee.total_users': 'Total utilisateurs',
    'employee.active': 'Actifs',
    'employee.suspended': 'Suspendus',
    'employee.administrators': 'Administrateurs',
    'employee.user_list': 'Liste des utilisateurs',
    'employee.user': 'Utilisateur',
    'employee.role': 'Rôle',
    'employee.status': 'Statut',
    'employee.actions': 'Actions',
    'employee.no_name': 'Sans nom',
    'employee.no_phone': 'Pas de téléphone',
    'employee.client': 'Client',
    'employee.admin': 'Administrateur',
    'employee.super_admin': 'Super Admin',
    'employee.vendor': 'Vendeur',
    'employee.relay': 'Point relais',
    'employee.search_placeholder': 'Rechercher un utilisateur...',
    'employee.all_roles': 'Tous les rôles',
    'employee.all_status': 'Tous les statuts',
    'employee.update': 'Actualiser',
    'employee.preferences': 'Préférences',
    'employee.statistics': 'Statistiques',
    'employee.last_connection': 'Dernière connexion',
    'employee.commands': 'Commandes',

    // Footer
    'footer.description': 'Votre marketplace de confiance à Kinshasa',
    'footer.quick_links': 'Liens rapides',
    'footer.categories': 'Catégories',
    'footer.contact': 'Contact',
    'footer.newsletter': 'Newsletter',
    'footer.newsletterTitle': 'Newsletter',
    'footer.emailPlaceholder': 'Votre email',
    'footer.about': 'À propos',
    'footer.how_it_works': 'Comment ça marche',
    'footer.become_vendor': 'Devenir vendeur',
    'footer.relay_points': 'Points relais',
    'footer.support': 'Support',

    // Orders
    'orders.recent': 'Commandes récentes',
    'orders.recent_desc': 'Suivez les commandes et leur progression',
    'orders.order': 'Commande',
    'orders.client': 'Client',
    'orders.vendor': 'Vendeur',
    'orders.relay_point': 'Point relais',
    'orders.amount': 'Montant',
    'orders.unknown_product': 'Produit inconnu',
    'orders.unknown_client': 'Client inconnu',
    'orders.unknown_vendor': 'Vendeur inconnu',
    'orders.unknown_relay': 'Point relais inconnu',
    'footer.copyright': '© Nazandoo. Tous droits réservés',
    'footer.terms': "Conditions d'utilisation",
    'footer.privacy': 'Politique de confidentialité',
    'footer.legal': 'Mentions légales',

    // Payment Methods
    'payments.title': 'Moyens de paiement',
    'payments.description': 'Gérez vos méthodes de paiement enregistrées',
    'payments.add': 'Ajouter',
    'payments.addTitle': 'Ajouter un moyen de paiement',
    'payments.addCardDescription': 'Enregistrez une nouvelle carte bancaire',
    'payments.addMobileMoneyDescription': 'Ajoutez un nouveau moyen de paiement Mobile Money',
    'payments.paymentType': 'Type de paiement',
    'payments.card': 'Carte bancaire',
    'payments.mobileMoney': 'Mobile Money',
    'payments.provider': 'Opérateur mobile',
    'payments.chooseProvider': 'Choisissez votre opérateur',
    'payments.orangeMoney': 'Orange Money',
    'payments.airtelMoney': 'Airtel Money',
    'payments.mpesa': 'Vodacom M-Pesa',
    'payments.africellMoney': 'Africell Money',
    'payments.phoneNumber': 'Numéro de téléphone',
    'payments.save': 'Enregistrer',
    'payments.cancel': 'Annuler',
    'payments.default': 'Par défaut',
    'payments.setAsDefault': 'Définir par défaut',
    'payments.delete': 'Supprimer',
    'payments.noMethods': 'Aucun moyen de paiement enregistré',
    'payments.addFirstMethod': 'Ajouter votre premier moyen de paiement',
    'payments.errorLoading': 'Erreur lors du chargement des moyens de paiement',
    'payments.fillAllFields': 'Veuillez remplir tous les champs',
    'payments.invalidPhone': 'Format de numéro invalide',
    'payments.methodAdded': 'Moyen de paiement ajouté',
    'payments.errorAdding': 'Erreur lors de l\'ajout',
    'payments.setAsDefaultSuccess': 'Moyen de paiement défini par défaut',
    'payments.errorSetDefault': 'Erreur lors de la mise à jour',
    'payments.methodDeleted': 'Moyen de paiement supprimé',
    'payments.errorDeleting': 'Erreur lors de la suppression',

    // Disputes
    'disputes.title': 'Mes réclamations',
    'disputes.description': 'Suivez l\'état de vos réclamations produits',
    'disputes.noDisputes': 'Aucune réclamation en cours',
    'disputes.order': 'Commande',
    'disputes.details': 'Détails',
    'disputes.detailsTitle': 'Détails de la réclamation',
    'disputes.reason': 'Motif',
    'disputes.description_label': 'Description',
    'disputes.status': 'Statut',
    'disputes.adminResponse': 'Réponse de l\'administrateur',
    'disputes.createdAt': 'Date de création',
    'disputes.resolvedAt': 'Date de résolution',
    'disputes.defectiveProduct': 'Produit défectueux',
    'disputes.wrongItem': 'Mauvais article',
    'disputes.missingItem': 'Article manquant',
    'disputes.damaged': 'Produit endommagé',
    'disputes.notAsDescribed': 'Non conforme à la description',
    'disputes.other': 'Autre',
    'disputes.pending': 'En attente',
    'disputes.inProgress': 'En cours',
    'disputes.resolved': 'Résolu',
    'disputes.rejected': 'Rejeté',
    'disputes.errorLoading': 'Erreur lors du chargement des réclamations',

    // Messaging
    'messaging.title': 'Messagerie',
    'messaging.description': 'Vos conversations avec les vendeurs et administrateurs',
    'messaging.noConversations': 'Aucune conversation',
    'messaging.unread': 'non lu(s)',
    'messaging.refreshing': 'Actualisation...',
    'messaging.pullToRefresh': 'Relâchez pour actualiser',
    'messaging.loading': 'Chargement...',
    'messaging.errorLoading': 'Erreur lors du chargement des messages',
    'messaging.vendor': 'Vendeur',

    // Categories
    'categories.title': 'Explorez nos catégories',
    'categories.description': 'Trouvez exactement ce que vous cherchez dans nos catégories variées',
    'categories.electronics': 'Électronique',
    'categories.fashion': 'Mode',
    'categories.home': 'Maison & Jardin',
    'categories.kids': 'Enfants & Bébés',
    'categories.auto': 'Auto & Moto',
    'categories.games': 'Jeux & Jouets',
    'categories.books': 'Livres',
    'categories.health': 'Beauté & Santé',
    'categories.sports': 'Sport & Loisirs',
    'categories.automobile': 'Automobile',
    'categories.products_count': 'produits',
    'categories.products_count_singular': 'produit',

    // Search
    'search.title': 'Rechercher des produits',
    'search.button': 'Rechercher',
    'search.results_for': 'Résultats pour',
    'search.searching': 'Recherche en cours...',
    'search.result': 'résultat',
    'search.results': 'résultats',
    'search.sort_by': 'Trier par',
    'search.sort.relevance': 'Pertinence',
    'search.sort.price_low': 'Prix croissant',
    'search.sort.price_high': 'Prix décroissant',
    'search.sort.name': 'Nom A-Z',
    'search.no_results': 'Aucun résultat trouvé',
    'search.try_different': 'Essayez avec des mots-clés différents',
    'search.browse_all': 'Parcourir tous les produits',
    'search.start_searching': 'Commencez votre recherche',
    'search.enter_keywords': 'Entrez des mots-clés pour trouver des produits',
    'search.in': 'dans',

    // Hero Section
    'hero.title': 'Marketplace Kinshasa',
    'hero.title.location': 'Votre marketplace locale',
    'hero.description': 'Découvrez les meilleurs produits locaux et internationaux directement à Kinshasa',
    'hero.explore_products': 'Explorer les produits',
    'hero.become_vendor': 'Devenir vendeur',
    'hero.fast_delivery': 'Livraison rapide',
    'hero.fast_delivery.desc': 'Livraison en 24-48h',
    'hero.secure_payment': 'Paiement sécurisé',
    'hero.secure_payment.desc': 'Transactions protégées',
    'hero.easy_pickup': 'Retrait facile',
    'hero.easy_pickup.desc': 'Points relais pratiques',
    'hero.secure_order': 'Commande sécurisée',
    'hero.satisfied_customers': 'Clients satisfaits',
    'hero.clients_satisfied_label': 'clients satisfaits',

    // Auth Page
    'auth.back_to_home': 'Retour à l\'accueil',
    'auth.access_account': 'Accédez à votre compte',
    'auth.connect_or_create': 'Connectez-vous ou créez un nouveau compte',
    'auth.signin': 'Connexion',
    'auth.signup': 'Inscription',
    'auth.email': 'Email',
    'auth.password': 'Mot de passe',
    'auth.full_name': 'Nom complet',
    'auth.email_placeholder': 'votre@email.com',
    'auth.password_placeholder': '••••••••',
    'auth.full_name_placeholder': 'Jean Mukendi',
    'auth.signin_button': 'Se connecter',
    'auth.signup_button': 'S\'inscrire',
    'auth.signin_loading': 'Connexion...',
    'auth.signup_loading': 'Inscription...',
    'auth.forgot_password': 'Mot de passe oublié ?',
    'auth.forgot_password_title': 'Mot de passe oublié',
    'auth.forgot_password_subtitle': 'Entrez votre email et nous vous enverrons un lien pour réinitialiser votre mot de passe',
    'auth.send_reset_link': 'Envoyer le lien',
    'auth.sending': 'Envoi...',
    'auth.back_to_login': 'Retour à la connexion',
    'auth.forgot_password_success_title': 'Email envoyé !',
    'auth.forgot_password_success_desc': 'Vérifiez votre boîte email pour réinitialiser votre mot de passe',
    'auth.forgot_password_error_title': 'Erreur',
    'auth.forgot_password_error_email': 'Veuillez entrer une adresse email valide',
    'auth.forgot_password_error_general': 'Une erreur est survenue. Veuillez réessayer.',
    'auth.forgot_password_email_sent': 'Un email de réinitialisation a été envoyé à votre adresse',
    'auth.forgot_password_check_spam': 'Si vous ne le trouvez pas, vérifiez votre dossier spam',
    'auth.send_another_email': 'Envoyer un autre email',
    'auth.forgot_password_help': 'Besoin d\'aide ? Contactez-nous à',
    'auth.terms_accept': 'En vous connectant, vous acceptez nos',
    'auth.terms_link': 'conditions d\'utilisation',
    'auth.privacy_link': 'politique de confidentialité',
    'auth.and': 'et notre',
    'auth.biometric_login': 'Connexion biométrique',
    'auth.use_biometric': 'Utiliser {type}',
    'auth.biometric_faceid': 'Face ID',
    'auth.biometric_touchid': 'Touch ID',
    'auth.biometric_fingerprint': 'Empreinte digitale',
    'auth.biometric_authenticate': 'Authentifiez-vous pour continuer',
    'auth.biometric_setup': 'Configurer la connexion biométrique',
    'auth.biometric_enabled': 'Connexion biométrique activée',
    'auth.biometric_error': 'Erreur d\'authentification biométrique',

    // Cart
    'cart.title': 'Panier',
    'cart.your_cart': 'Votre panier',
    'cart.product': 'Produit',
    'cart.products_in_cart': 'produit(s) dans votre panier',
    'cart.empty': 'Votre panier est vide',
    'cart.start_shopping': 'Commencer vos achats',
    'cart.quantity': 'Quantité',
    'cart.price': 'Prix',
    'cart.subtotal': 'Sous-total',
    'cart.total': 'Total',
    'cart.checkout': 'Passer commande',
    'cart.select_relay': 'Sélectionner un point relais',
    'cart.relay_required': 'Veuillez sélectionner un point relais',
    'cart.no_relay_available': 'Aucun point relais disponible',
    'cart.payment_processing': 'Traitement du paiement en cours...',
    'cart.order_success': 'Commande créée avec succès',
    'cart.order_error': 'Erreur lors de la création de la commande',
    'cart.payment_error': 'Erreur lors du paiement',
    'cart.payment_cancelled': 'Paiement annulé',
    'cart.payment_success': 'Paiement réussi',
    'cart.fees': 'Frais',
    'cart.totalToPay': 'Total à payer',
    'cart.productsInCart': 'produit(s) dans votre panier',
    'cart.by': 'par',
    'cart.vendor': 'Vendeur',
    'cart.pickupPoint': 'Point de retrait',
    'cart.loading': 'Chargement...',
    'cart.chooseRelay': 'Choisir un point relais',
    'cart.loadingRelays': 'Chargement des points relais...',
    'cart.noRelays': 'Aucun point relais disponible',
    'cart.processing': 'Traitement...',
    'cart.placeOrder': 'Passer commande',
    'cart.continue': 'Continuer les achats',
    'cart.continueShopping': 'Continuer les achats',
    'cart.clear': 'Vider',
    'cart.error': 'Erreur',
    'cart.errorLoadingRelays': 'Erreur lors du chargement des points relais',
    'cart.orderPrepared': 'Commande préparée',
    'cart.choosePayment': 'Choisissez votre mode de paiement',
    'cart.orderConfirmed': 'Commande confirmée',
    'cart.paymentSuccess': 'Paiement réussi',

    // Payment
    'payment.bankCard': 'Carte bancaire',
    'payment.securePayment': 'Paiement sécurisé par Stripe',
    'payment.instantPayment': 'Paiement instantané',
    'payment.mobileMoney': 'Mobile Money',
    'payment.secureMobilePayment': 'Paiement mobile sécurisé',
    'payment.popularInDRC': 'Populaire en RDC',
    'payment.processing': 'Traitement...',
    'payment.payByCard': 'Payer par carte',
    'payment.payByMobile': 'Payer par mobile',
    'payment.secureCheckout': 'Paiement sécurisé',
    'payment.loadingCheckout': 'Chargement du paiement...',

    // Product Detail
    'product.back': 'Retour',
    'product.brand': 'Marque',
    'product.model': 'Modèle',
    'product.noReviewsYet': 'Pas encore d\'avis',
    'product.reviews': 'avis',
    'product.inStock': 'En stock',
    'product.available': 'disponibles',

    // Conversation View
    'conversationView.noMessages': 'Aucun message',
    'conversationView.writePlaceholder': 'Écrivez votre message...',
    'conversationView.sendHint': 'Appuyez sur Entrée pour envoyer',
    'product.outOfStock': 'Rupture de stock',
    'product.quantity': 'Quantité',
    'product.addToCart': 'Ajouter au panier',
    'product.addedToCart': 'Produit ajouté au panier',
    'product.deliveryAvailable': 'Livraison disponible en point relais',
    'product.securePayment': 'Paiement sécurisé',
    'product.returnPolicy': 'Retour possible sous 14 jours',
    'product.description': 'Description',
    'product.noDescription': 'Aucune description disponible pour ce produit.',
    'product.specifications': 'Spécifications',
    'product.reviews_tab': 'Avis',
    'product.relatedProducts': 'Produits similaires',
    'product.notFound': 'Produit non trouvé',
    'product.category': 'Catégorie',
    'product.stock': 'Stock',
    'product.units': 'unités',
    'product.reference': 'Référence',
    'product.dateAdded': 'Date d\'ajout',
    'product.favorites': 'Favoris',
    'product.share': 'Partager',
    'product.notSpecified': 'Non spécifiée',
    'product.by': 'par',
    'product.seller': 'Vendeur',
    'product.reviewsComingSoon': 'Les avis clients seront bientôt disponibles.',
    'product.addedToFavorites': 'Ajouté aux favoris',
    'product.removedFromFavorites': 'Retiré des favoris',
    'product.favoriteError': 'Erreur lors de la gestion des favoris',
    'product.shareText': 'Découvrez ce produit sur notre marketplace',
    'product.linkCopied': 'Lien copié dans le presse-papier',
    'product.shareError': 'Erreur lors du partage',

    // Account Settings
    'account.profile_settings': 'Paramètres du profil',
    'account.manage_info': 'Gérez vos informations personnelles',
    'account.full_name': 'Nom complet',
    'account.phone': 'Téléphone',
    'account.business_name': 'Nom de l\'entreprise',
    'account.business_address': 'Adresse de l\'entreprise',
    'account.update_profile': 'Mettre à jour le profil',
    'account.updating': 'Mise à jour...',
    'account.profile_updated': 'Profil mis à jour avec succès',
    'account.profile_error': 'Erreur lors de la mise à jour du profil',
    'account.email_settings': 'Paramètres email',
    'account.manage_email': 'Gérez votre adresse email',
    'account.new_email': 'Nouvel email',
    'account.update_email': 'Mettre à jour l\'email',
    'account.email_updated': 'Email mis à jour avec succès',
    'account.email_error': 'Erreur lors de la mise à jour de l\'email',
    'account.password_settings': 'Paramètres du mot de passe',
    'account.change_password': 'Changez votre mot de passe',
    'account.new_password': 'Nouveau mot de passe',
    'account.confirm_password': 'Confirmer le mot de passe',
    'account.update_password': 'Mettre à jour le mot de passe',
    'account.password_updated': 'Mot de passe mis à jour avec succès',
    'account.password_error': 'Erreur lors de la mise à jour du mot de passe',
    'account.notification_settings': 'Paramètres de notification',
    'account.manage_notifications': 'Gérez vos préférences de notification',
    'account.email_notifications': 'Notifications par email',
    'account.email_notifications_desc': 'Recevoir des emails pour les mises à jour importantes',
    'account.sms_notifications': 'Notifications SMS',
    'account.sms_notifications_desc': 'Recevoir des SMS pour les mises à jour de commande',

    // Account Settings (Dashboard)
    'accountSettings.personalInfo': 'Informations personnelles',
    'accountSettings.manageProfile': 'Gérez vos informations de profil',
    'accountSettings.fullName': 'Nom complet',
    'accountSettings.phone': 'Téléphone',
    'accountSettings.businessName': 'Nom de l\'entreprise',
    'accountSettings.notProvided': 'Non renseigné',
    'accountSettings.editInfo': 'Modifier les informations',
    'accountSettings.businessNamePlaceholder': 'Ex: Ma boutique',
    'accountSettings.saving': 'Enregistrement...',
    'accountSettings.save': 'Enregistrer',
    'accountSettings.cancel': 'Annuler',
    'accountSettings.emailAddress': 'Adresse email',
    'accountSettings.modifyEmail': 'Modifiez votre adresse email',
    'accountSettings.currentEmail': 'Email actuel',
    'accountSettings.changeEmail': 'Changer l\'email',
    'accountSettings.newEmail': 'Nouvel email',
    'accountSettings.important': 'Important',
    'accountSettings.emailChangeNote': 'Vous recevrez un email de confirmation pour valider votre nouvelle adresse.',
    'accountSettings.updating': 'Mise à jour...',
    'accountSettings.updateEmail': 'Mettre à jour l\'email',
    'accountSettings.password': 'Mot de passe',
    'accountSettings.changePassword': 'Changez votre mot de passe',
    'accountSettings.newPassword': 'Nouveau mot de passe',
    'accountSettings.confirmPassword': 'Confirmer le mot de passe',
    'accountSettings.updatePassword': 'Mettre à jour le mot de passe',
    'accountSettings.profileSettings': 'Préférences de profil',
    'accountSettings.customizeExperience': 'Personnalisez votre expérience',
    'accountSettings.language': 'Langue',
    'accountSettings.currency': 'Devise',
    'dashboard.viewDetails': 'Voir détails',
    'dashboard.cancelOrder': 'Annuler la commande',
    'dashboard.cancelOrderTitle': 'Annuler la commande ?',
    'dashboard.cancelOrderConfirm': 'Voulez-vous vraiment annuler la commande',
    'dashboard.cancelOrderWarning': 'Cette action est définitive et ne peut pas être annulée.',
    'dashboard.back': 'Retour',
    'dashboard.confirmCancel': 'Confirmer l\'annulation',

    // Conversation View
    'conversation.messages': 'Messages',
    'conversation.message_center': 'Centre de messages',
    'conversation.all_conversations': 'Toutes les conversations',
    'conversation.no_messages': 'Aucun message',
    'conversation.start_conversation': 'Commencez une conversation',
    'conversation.loading': 'Chargement des messages...',
    'conversation.type_message': 'Tapez votre message...',
    'conversation.send': 'Envoyer',
    'conversation.reply': 'Répondre',
    'conversation.subject': 'Sujet',
    'conversation.from': 'De',
    'conversation.to': 'À',
    'conversation.date': 'Date',
    'conversation.order': 'Commande',

    // Vendors
    'vendor.manage_vendors': 'Gérez les demandes et le statut des vendeurs',
    'vendor.applications': 'Demandes de vendeurs',
    'vendor.review_applications': 'Examinez et traitez les demandes d\'inscription des nouveaux vendeurs',
    'applications': 'Demandes',

    // Vendor Account translations
    'vendor.title': 'Espace Vendeur',
    'vendor.subtitle': 'Gérez votre boutique et vos produits',
    'vendor.refresh': 'Actualiser',
    'vendorAccount.loading': 'Chargement de votre espace vendeur...',
    'vendorAccount.refreshing': 'Actualisation...',
    'vendorAccount.products': 'Produits',
    'vendorAccount.orders': 'Commandes',
    'vendorAccount.revenue': 'Revenus',
    'vendorAccount.lowStock': 'Stock Faible',
    'vendorAccount.activeProducts': 'actifs',
    'vendorAccount.toProcess': 'à traiter',
    'vendorAccount.totalSales': 'Total des ventes',
    'vendorAccount.productsLowStock': 'produits en rupture',
    'vendorAccount.stockTotal': 'Stock total:',
    'vendorAccount.units': 'unités',
    'vendorAccount.lowStockAlert': 'produits avec un stock faible nécessitent votre attention',
    'vendorAccount.pendingDropAlert': 'commandes en attente de dépôt',
    'vendorAccount.stock': 'Stock',
    'vendorAccount.team': 'Équipe',
    'vendorAccount.settings': 'Paramètres',
    'vendorAccount.analytics': 'Statistiques',
    'vendor.product_catalog': 'Catalogue de produits',
    'vendor.manage_inventory': 'Gérez votre inventaire et vos stocks',
    'vendor.new_product': 'Nouveau produit',
    'product.product': 'Produit',
    'product.price_label': 'Prix',
    'product.status': 'Statut',
    'product.actions': 'Actions',
    'product.lastUpdate': 'Dernière mise à jour',
    'product.active': 'Actif',
    'product.inactive': 'Inactif',
    'product.noCatalog': 'Aucun produit dans votre catalogue',
    'product.addFirst': 'Ajoutez votre premier produit pour commencer',
    'product.edit': 'Modifier le produit',
    'product.new': 'Nouveau produit',
    'product.formDesc': 'Remplissez les informations de votre produit',
    'product.name': 'Nom du produit',
    'product.price': 'Prix',
    'product.currency': 'Devise',
    'product.selectCurrency': 'Choisir la devise',
    'product.selectCategory': 'Choisir une catégorie',
    'product.cancel': 'Annuler',
    'product.modify': 'Modifier',
    'product.create': 'Créer',
    'product.multilingualInfo': 'Informations multilingues',
    'product.translating': 'Traduction en cours...',
    'product.autoTranslate': 'Traduire automatiquement',
    'product.translationHint': 'Remplissez le titre dans votre langue, puis cliquez sur "Traduire automatiquement" pour générer les autres langues. Vous pourrez ensuite les modifier manuellement.',
    'product.sourceLanguage': 'Langue source',
    'product.namePlaceholder': 'Nom du produit',
    'product.descriptionPlaceholder': 'Description détaillée du produit',
    'product.translationComplete': 'Traduction complète',
    'product.translationPartial': 'Traduction partielle',
    'product.translationSuccessMessage': 'Traductions générées avec succès ! Vérifiez et ajustez si nécessaire.',
    'orders.track': 'Suivez et gérez vos commandes',
    'orders.noOrders': 'Aucune commande pour le moment',
    'orders.product': 'Produit',
    'orders.pickupPoint': 'Point de retrait',
    'orders.pickedUp': 'Récupérées',
    'orders.awaitingDropOff': 'En attente de dépôt',
    'stock.title': 'Gestion des stocks',
    'stock.description': 'Vue d\'ensemble de vos stocks. Total:',
    'stock.units': 'unités',
    'stock.lowStock': 'Stock faible (≤5)',
    'stock.noLowStock': 'Aucun produit en stock faible',
    'stock.stockByCategory': 'Stock par catégorie',
    'stock.products': 'produits',
    'stock.stockDetails': 'Détail des stocks',
    'stock.currentStock': 'Stock actuel',
    'stock.state': 'État',
    'stock.critical': 'Critique',
    'stock.low': 'Faible',
    'stock.good': 'Bon',
    'team.title': 'Gestion d\'équipe',
    'team.description': 'Gérez les gestionnaires de votre magasin',
    'team.addManager': 'Ajouter un gestionnaire',
    'team.noManagers': 'Aucun gestionnaire assigné',
    'team.addFromEmployees': 'Ajoutez des gestionnaires depuis vos employés',
    'team.addManagerDialog': 'Ajouter un gestionnaire',
    'team.selectEmployee': 'Sélectionnez un employé à promouvoir gestionnaire',
    'team.employee': 'Employé',
    'team.chooseEmployee': 'Choisir un employé',
    'team.cancel': 'Annuler',
    'team.add': 'Ajouter',
    'settings.title': 'Paramètres du magasin',
    'settings.description': 'Configurez les informations de votre magasin',
    'settings.shopName': 'Nom du magasin',
    'settings.shopNamePlaceholder': 'Ex: Boutique Centrale',
    'settings.contactEmail': 'Email de contact',
    'settings.contactEmailPlaceholder': 'contact@monmagasin.cd',
    'settings.businessPhone': 'Téléphone du magasin',
    'settings.businessPhonePlaceholder': 'Ex: +243812345678',
    'settings.mainAddress': 'Adresse principale',
    'settings.mainAddressPlaceholder': 'Adresse complète de votre magasin',
    'settings.businessHours': 'Heures d\'ouverture',
    'settings.monday': 'Lundi',
    'settings.tuesday': 'Mardi',
    'settings.wednesday': 'Mercredi',
    'settings.thursday': 'Jeudi',
    'settings.friday': 'Vendredi',
    'settings.saturday': 'Samedi',
    'settings.sunday': 'Dimanche',
    'settings.open': 'Ouvert',
    'settings.opening': 'Ouverture',
    'settings.closing': 'Fermeture',
    'settings.closed': 'Fermé',
    'settings.saveInfo': 'Sauvegarder les informations',
      'settings.withdrawalDefaults': 'Paramètres de retrait par défaut',
      'settings.withdrawalDefaultsDesc': 'Configurez vos coordonnées pour les retraits (mobile money ou virement bancaire)',
      'settings.mobileNumber': 'Numéro de téléphone mobile',
      'settings.mobileNumberPlaceholder': 'Ex: +243812345678',
      'settings.mobileProvider': 'Fournisseur de paiement mobile',
      'settings.selectMobileProvider': 'Sélectionner un fournisseur',
      'settings.bankAccount': 'Numéro de compte bancaire',
      'settings.bankAccountPlaceholder': 'Ex: 1234567890',
      'settings.bankName': 'Nom de la banque',
      'settings.bankNamePlaceholder': 'Ex: BCDC, Equity Bank, etc.',
    'analytics.description': 'Suivez les performances de votre magasin',
    'analytics.totalRevenue': 'Revenus totaux',
    'analytics.sinceBeginning': 'Depuis le début',
    'analytics.ordersByStatus': 'Commandes par statut',
    'status.pending_payment': 'En attente de paiement',
    'status.paid': 'Payé',
    'status.awaiting_drop_off': 'En attente de dépôt',
    'status.available_for_pickup': 'Disponible pour retrait',
    'status.picked_up': 'Récupéré',
    'status.expired': 'Expiré',
    'status.dispute': 'Litige',
    'currency.cdf_full': 'Franc Congolais (CDF)',
    'currency.usd_full': 'Dollar Américain (USD)',
    'currency.eur_full': 'Euro (EUR)',
    'category.electronique': 'Électronique',
    'category.mode': 'Mode',
    'category.maison': 'Maison & Jardin',
    'category.sport': 'Sport & Loisirs',
    'category.livre': 'Livres',
    'category.beaute': 'Beauté & Santé',
    'vendorAccount.dataUpdated': 'Données mises à jour',
    'vendorAccount.dataUpdatedDesc': 'Toutes vos données ont été actualisées',
    'vendorAccount.loadingError': 'Erreur de chargement',
    'vendor.filters.title': 'Filtres, recherche et tri',
    'vendor.filters.reset': 'Réinitialiser',
    'vendor.filters.status': 'Statut',
    'vendor.filters.allStatuses': 'Tous les statuts',
    'vendor.filters.search': 'Recherche',
    'vendor.filters.searchPlaceholder': 'N° produit, titre, catégorie...',
    'vendor.filters.period': 'Période',
    'vendor.filters.allPeriods': 'Toutes les périodes',
    'vendor.filters.today': 'Aujourd\'hui',
    'vendor.filters.week': 'Cette semaine',
    'vendor.filters.month': 'Ce mois',
    'vendor.filters.year': 'Cette année',
    'vendor.filters.sortBy': 'Trier par',
    'vendor.filters.createdAt': 'Date de création',
    'vendor.filters.name': 'Nom',
    'vendor.filters.price': 'Prix',
    'vendor.filters.stock': 'Stock',
    'vendor.filters.resultsFound': 'produits trouvés',
    'vendor.filters.resultFound': 'produit trouvé',
    'vendorAccount.loadingErrorDesc': 'Impossible de charger les données',
    'vendorAccount.productUpdated': 'Produit mis à jour',
    'vendorAccount.productCreated': 'Produit créé',
    'vendorAccount.error': 'Erreur',
    'vendorAccount.confirmDelete': 'Êtes-vous sûr de vouloir supprimer ce produit ?',
    'vendorAccount.productDeleted': 'Produit supprimé',
    'unauthorizedAccess': 'Accès non autorisé',

    // Admin - Bulk Translation
    'admin.bulkTranslation.title': 'Traduction en masse des produits',
    'admin.bulkTranslation.description': 'Traduisez automatiquement tous les produits non traduits dans les 4 langues',
    'admin.bulkTranslation.untranslated': 'non traduits',
    'admin.bulkTranslation.allTranslated': 'Tous les produits sont traduits !',
    'admin.bulkTranslation.productsNeedTranslation': 'produits nécessitent une traduction',
    'admin.bulkTranslation.progress': 'Progression',
    'admin.bulkTranslation.translating': 'Traduction en cours',
    'admin.bulkTranslation.completed': 'Réussis',
    'admin.bulkTranslation.failed': 'Échoués',
    'admin.bulkTranslation.translatingButton': 'Traduction en cours...',
    'admin.bulkTranslation.startButton': 'Traduire tous les produits',
    'admin.bulkTranslation.refreshButton': 'Actualiser',
    'admin.bulkTranslation.note': 'Note',
    'admin.bulkTranslation.noteText': 'La traduction peut prendre plusieurs minutes selon le nombre de produits. Chaque produit sera traduit dans les 3 langues manquantes (EN, LG, SW).',

    // Admin - Errors
    'admin.error.loading_users': 'Erreur chargement utilisateurs',
    'admin.error.loading_vendor_apps': 'Erreur chargement demandes vendeurs',
    'admin.error.loading_employees': 'Erreur chargement employés',
    'admin.error.loading_relays': 'Erreur chargement points relais',
    'admin.error.loading_orders': 'Erreur chargement commandes',
    'admin.error.loading_settings': 'Erreur chargement paramètres',
    'admin.error.loading_categories': 'Erreur chargement catégories',
    'admin.error.loading_footer': 'Erreur chargement sections footer',
    'admin.error.loading_cms': 'Erreur chargement contenu CMS',
    'admin.error.loading_visitors': 'Erreur chargement détails visiteurs',

    // Admin - Employee Management
    'admin.employee.email_password_required': 'Email et mot de passe requis pour créer un compte utilisateur',
    'admin.employee.password_requirements': 'Le mot de passe doit contenir au minimum 10 caractères avec : majuscule, minuscule, chiffre et caractère spécial',
    'admin.employee.updated_successfully': 'Employé modifié avec succès',
    'admin.employee.created_successfully': 'Employé créé avec succès',
    'admin.employee.with_user_account': ' avec compte utilisateur',
    'admin.employee.confirm_delete': 'Êtes-vous sûr de vouloir supprimer cet employé ?',
    'admin.employee.deleted_successfully': 'Employé supprimé avec succès',

    // Admin - Relay Management
    'admin.relay.updated_successfully': 'Point relais modifié avec succès',
    'admin.relay.created_successfully': 'Point relais créé avec succès',
    'admin.relay.confirm_delete': 'Êtes-vous sûr de vouloir supprimer ce point relais ?',
    'admin.relay.deleted_successfully': 'Point relais supprimé avec succès',

    // Admin - Translations Page
    'admin.translations.page_title': 'Gestion des traductions',
    'admin.translations.page_description': 'Gérez les traductions multi-langues de votre marketplace',
    'admin.translations.auto_migration': 'Migration automatique',
    'admin.translations.migration_description': 'Migrez toutes les traductions vers la base de données',
    'admin.translations.supported_languages': 'Langues supportées',
    'admin.translations.available_languages_desc': 'Langues actuellement disponibles',
    'admin.translations.french': 'Français',
    'admin.translations.english': 'English',
    'admin.translations.primary_language': 'Langue principale',
    'admin.translations.secondary_language': 'Langue secondaire',
    'admin.translations.active_status': 'Actif',

    // Checkout Flow
    'checkout.finalize_order': 'Finaliser votre commande',
    'checkout.products_count': 'produit(s)',
    'checkout.choose_relay_point': 'Choisissez votre point relais de retrait',
    'checkout.choose_payment_method': 'Choisissez votre mode de paiement',
    'checkout.relay_point': 'Point relais',
    'checkout.payment': 'Paiement',
    'checkout.order_summary': 'Résumé de la commande',
    'checkout.promo_code_placeholder': 'Code promo',
    'checkout.remove_promo': 'Retirer',
    'checkout.subtotal': 'Sous-total',

    // All Products Page
    'allProducts.backHome': 'Retour à l\'accueil',
    'allProducts.title': 'Tous nos produits',
    'allProducts.description': 'Découvrez notre gamme complète de {count} produits',
    'allProducts.searchPlaceholder': 'Rechercher des produits...',
    'allProducts.refreshing': 'Actualisation...',
    'allProducts.pullToRefresh': 'Relâchez pour actualiser',

    // Become Vendor Page
    'becomeVendor.login_required': 'Connexion requise',
    'becomeVendor.login_required_message': 'Vous devez créer un compte et vous connecter avant de soumettre une candidature vendeur.',
    'becomeVendor.fill_required_fields': 'Veuillez remplir tous les champs obligatoires',
    'becomeVendor.accept_terms': 'Veuillez accepter les conditions générales',
    'becomeVendor.attach_identity': 'Veuillez joindre une pièce d\'identité',
    'becomeVendor.application_sent': 'Demande envoyée avec succès !',
    'becomeVendor.application_under_review': 'Votre demande de devenir vendeur est en cours d\'examen. Nous vous contacterons bientôt.',
    'becomeVendor.application_pending': 'Candidature en cours de validation',
    'becomeVendor.review_message': 'Notre équipe examine votre dossier. Vous recevrez une réponse sous 24-48h.',

    // Deals Page
    'deals.title': 'Deals du jour',
    'deals.description': 'Profitez de réductions exceptionnelles à durée limitée',
    'deals.all_deals': 'Tous les deals',
    'deals.featured_deals': 'Deals en vedette',
    'deals.no_deals': 'Aucun deal disponible',
    'deals.no_deals_message': 'Revenez bientôt pour découvrir nos prochaines offres',
    'deals.featured': 'En vedette',
    'deals.lowest_price': 'Le plus bas :',
    'deals.expires_soon': 'Expire bientôt',
    'deals.days_left': 'j',
    'deals.hours_left': 'h restantes',

    // Vendor sections - Stock Management
    'vendor.stock.management': 'Gestion de stock',
    'vendor.stock.overview.totalProducts': 'Produits totaux',
    'vendor.stock.overview.lowStock': 'Stock faible',
    'vendor.stock.overview.outOfStock': 'Rupture de stock',
    'vendor.stock.overview.stockValue': 'Valeur du stock',
    'vendor.stock.lowStockLabel': 'Stock faible (≤5)',
    'vendor.stock.noLowStock': 'Aucun produit avec stock faible',
    'vendor.stock.stockByCategory': 'Stock par catégorie',
    'vendor.stock.units': 'unités',
    'vendor.stock.alerts.outOfStockProducts': 'Produits en rupture de stock',
    'vendor.stock.alerts.lowStockProducts': 'Stock faible (≤ 5 unités)',
    'vendor.stock.actions.restock': 'Réapprovisionner',
    'vendor.stock.actions.adjust': 'Ajuster',
    'vendor.stock.inventory.title': 'Inventaire des produits',
    'vendor.stock.inventory.description': 'Gérez les niveaux de stock de vos produits',
    'vendor.stock.table.product': 'Produit',
    'vendor.stock.table.category': 'Catégorie',
    'vendor.stock.table.currentStock': 'Stock actuel',
    'vendor.stock.table.unitValue': 'Valeur unitaire',
    'vendor.stock.table.totalValue': 'Valeur totale',
    'vendor.stock.table.actions': 'Actions',
    'vendor.stock.uncategorized': 'Non catégorisé',
    'vendor.stock.dialog.title': 'Ajuster le stock',
    'vendor.stock.dialog.adjustmentType': 'Type d\'ajustement',
    'vendor.stock.dialog.stockIn': 'Entrée (+)',
    'vendor.stock.dialog.stockOut': 'Sortie (-)',
    'vendor.stock.dialog.adjustment': 'Correction',
    'vendor.stock.dialog.quantity': 'Quantité',
    'vendor.stock.dialog.newStock': 'Nouveau stock',
    'vendor.stock.dialog.reason': 'Motif (optionnel)',
    'vendor.stock.dialog.reasonPlaceholder': 'Ex: Réapprovisionnement, retour client, correction...',
    'vendor.stock.dialog.apply': 'Appliquer l\'ajustement',
    'vendor.stock.success.updated': 'Stock mis à jour',
    'vendor.stock.error.loading': 'Erreur lors du chargement',
    'vendor.stock.error.loadingProducts': 'Erreur lors du chargement des produits',
    'vendor.stock.error.loadingTransactions': 'Erreur lors du chargement des transactions',
    'vendor.stock.error.invalidQuantity': 'Veuillez renseigner une quantité valide.',
    'unauthorizedDesc': 'Vous n\'avez pas les permissions nécessaires pour accéder à cet espace',
    'none': 'Aucun',
    'unspecified': 'Non spécifié',
    'write_comment': 'Écrire un commentaire',

    // Products Section
    'products.title': 'Produits',
    'products.description': 'Découvrez les articles les plus appréciés',
    'products.no_products': 'Aucun produit disponible',
    'products.view_all': 'Voir tous les produits',
    'analytics.title': 'Statistiques',
    'managers': 'Gestionnaires'
  },
  en: {
    'home': 'Home',
    'products': 'Products',
    'categories': 'Categories',
    'login': 'Login',
    'logout': 'Logout',
    'register': 'Register',
    'dashboard': 'Dashboard',
    'admin': 'Administration',
    'settings': 'Settings',
    'profile': 'Profile',
    'orders': 'Orders',
    'cart': 'Cart',
    'search': 'Search',
    'loading': 'Loading...',
    'error': 'Error',
    'success': 'Success',
    'cancel': 'Cancel',
    'confirm': 'Confirm',
    'save': 'Save',
    'edit': 'Edit',
    'delete': 'Delete',
    'add': 'Add',
    'create': 'Create',
    'update': 'Update',
    'view': 'View',
    'close': 'Close',
    'open': 'Open',
    'yes': 'Yes',
    'no': 'No',
    'next': 'Next',
    'previous': 'Previous',
    'continue': 'Continue',
    'back': 'Back',
    'submit': 'Submit',
    'reset': 'Reset',
    'filter': 'Filter',
    'sort': 'Sort',
    'name': 'Name',
    'email': 'Email',
    'phone': 'Phone',
    'address': 'Address',
    'city': 'City',
    'country': 'Country',
    'date': 'Date',
    'time': 'Time',
    'status': 'Status',
    'active': 'Active',
    'inactive': 'Inactive',
    'pending': 'Pending',
    'approved': 'Approved',
    'rejected': 'Rejected',
    'completed': 'Completed',
    'cancelled': 'Cancelled',
    'total': 'Total',
    'price': 'Price',
    'quantity': 'Quantity',
    'description': 'Description',
    'category': 'Category',
    'language': 'Language',
    'currency': 'Currency',

    // Header
    'header.hello': 'Hello',
    'header.menu': 'Menu',
    'header.home': 'Home',
    'header.all_products': 'All products',
    'header.your_orders': 'Your orders',
    'header.your_account': 'Your account',
    'header.vendor_account': 'Vendor account',
    'header.admin_panel': 'Administration',
    'header.points': 'Relay Points',
    'header.become_vendor': 'Become a vendor',
    'header.relay_management': 'Relay management',
    'header.create_business': 'Create my business',
    'header.cart': 'Cart',
    'header.logout': 'Logout',
    'header.login': 'Login',
    'header.deals': 'Deals',
    'header.buyer_account': 'Buyer account',
    'header.my_profile': 'My profile',
    'header.my_orders': 'My orders',
    'header.vendor_dashboard': 'Dashboard',
    'header.relay_dashboard': 'Relay Point',
    'header.admin_account': 'Admin account',
    'header.administration': 'Administration',
    'header.search.placeholder': 'Search for a product...',

    // Site texts
    'site.tagline': 'Your local marketplace',
    'site.description': 'Discover the best local and international products directly in Kinshasa',

    // Auth Page
    'auth.back_to_home': 'Back to home',
    'auth.access_account': 'Access your account',
    'auth.connect_or_create': 'Sign in or create a new account',
    'auth.signin': 'Sign In',
    'auth.signup': 'Sign Up',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.full_name': 'Full Name',
    'auth.email_placeholder': 'your@email.com',
    'auth.password_placeholder': '••••••••',
    'auth.full_name_placeholder': 'John Doe',
    'auth.signin_button': 'Sign In',
    'auth.signup_button': 'Sign Up',
    'auth.signin_loading': 'Signing in...',
    'auth.signup_loading': 'Signing up...',
    'auth.forgot_password': 'Forgot password?',
    'auth.forgot_password_title': 'Forgot Password',
    'auth.forgot_password_subtitle': 'Enter your email and we\'ll send you a link to reset your password',
    'auth.send_reset_link': 'Send Reset Link',
    'auth.sending': 'Sending...',
    'auth.back_to_login': 'Back to login',
    'auth.forgot_password_success_title': 'Email Sent!',
    'auth.forgot_password_success_desc': 'Check your email inbox to reset your password',
    'auth.forgot_password_error_title': 'Error',
    'auth.forgot_password_error_email': 'Please enter a valid email address',
    'auth.forgot_password_error_general': 'An error occurred. Please try again.',
    'auth.forgot_password_email_sent': 'A reset email has been sent to your address',
    'auth.forgot_password_check_spam': 'If you don\'t find it, check your spam folder',
    'auth.send_another_email': 'Send another email',
    'auth.forgot_password_help': 'Need help? Contact us at',
    'auth.terms_accept': 'By logging in, you accept our',
    'auth.terms_link': 'terms of service',
    'auth.privacy_link': 'privacy policy',
    'auth.and': 'and our',
    'auth.biometric_login': 'Biometric login',
    'auth.use_biometric': 'Use {type}',
    'auth.biometric_faceid': 'Face ID',
    'auth.biometric_touchid': 'Touch ID',
    'auth.biometric_fingerprint': 'Fingerprint',
    'auth.biometric_authenticate': 'Authenticate to continue',
    'auth.biometric_setup': 'Set up biometric login',
    'auth.biometric_enabled': 'Biometric login enabled',
    'auth.biometric_error': 'Biometric authentication error',

    // Admin
    'admin.title': 'Administration',
    'admin.subtitle': 'Admin dashboard',
    'admin.system_operational': 'System operational',
    'admin.tabs.dashboard': 'Dashboard',
    'admin.tabs.users': 'Users',
    'admin.tabs.vendors': 'Vendors',
    'admin.tabs.employees': 'Employees',
    'admin.tabs.relays': 'Relay points',
    'admin.tabs.orders': 'Orders',
    'admin.tabs.cms': 'CMS',
    'admin.tabs.config': 'Configuration',
    'admin.tabs.roles': 'Roles',
    'admin.tabs.activity': 'Activity',
    'admin.tabs.bulk': 'Bulk actions',
    'admin.quick_actions': 'Quick actions',
    'admin.new_category': 'New category',
    'admin.new_page': 'New page',
    'admin.new_footer_section': 'New footer section',
    'admin.cms.loading': 'Loading...',
    'admin.cms.editor': 'Editor',
    'admin.cms.preview': 'Preview',
    'admin.cms.save': 'Save',
    'admin.cms.hero_tab': 'Hero',
    'admin.cms.sections_tab': 'Sections',
    'admin.cms.faq_tab': 'FAQ',
    'admin.cms.hero_title': 'Hero Title',
    'admin.cms.hero_description': 'Hero Description',
    'admin.cms.hero_title_placeholder': 'Main page title',
    'admin.cms.hero_description_placeholder': 'Description under the title',
    'admin.cms.section_title': 'Section Title',
    'admin.cms.section_content': 'Content',
    'admin.cms.section_title_placeholder': 'Section title',
    'admin.cms.section_content_placeholder': 'Section content',
    'admin.cms.faq_title': 'Frequently Asked Questions',
    'admin.cms.add_faq': 'Add Question',
    'admin.cms.question': 'Question',
    'admin.cms.answer': 'Answer',
    'admin.cms.question_placeholder': 'Enter question',
    'admin.cms.answer_placeholder': 'Enter answer',
    'admin.cms.saved': 'Content Saved',
    'admin.cms.saved_description': 'Changes have been saved successfully',
    'admin.cms.error': 'Error',
    
    // CMS Configuration
    'admin.config.title': 'Site configuration',
    'admin.config.subtitle': 'Full interface to customize your marketplace',
    'admin.config.site_content': 'Site content',
    'admin.config.test_mode': 'Test Mode Stock',
    'admin.config.system_settings': 'System settings',
    'admin.config.dashboard': 'Dashboard',
    'admin.config.hero': 'Hero',
    'admin.config.categories': 'Categories',
    'admin.config.pages': 'Pages',
    'admin.config.menus': 'Menus',
    'admin.config.footer': 'Footer',
    'admin.config.active_categories': 'Active categories',
    'admin.config.total_categories': 'Total categories',
    'admin.config.category_management': 'Category Management',
    'admin.config.category_management_desc': 'Customize product categories displayed on your marketplace',
    'admin.config.category_active': 'Active',
    'admin.config.category_inactive': 'Inactive',
    'admin.config.products': 'product(s)',
    'admin.config.edit_category': 'Edit category',
    'admin.config.delete_category': 'Delete category',
    'admin.config.delete_confirm': 'Are you sure you want to delete this category?',
    'admin.config.category_deleted': 'Category deleted successfully',

    // Product Detail
    'product.back': 'Back',
    'product.brand': 'Brand',
    'product.model': 'Model',
    'product.noReviewsYet': 'No reviews yet',
    'product.reviews': 'reviews',
    'product.inStock': 'In stock',

    // Conversation View
    'conversationView.noMessages': 'No messages',
    'conversationView.writePlaceholder': 'Write your message...',
    'conversationView.sendHint': 'Press Enter to send',
    'product.available': 'available',
    'product.outOfStock': 'Out of stock',
    'product.quantity': 'Quantity',
    'product.addToCart': 'Add to cart',
    'product.addedToCart': 'Product added to cart',
    'product.deliveryAvailable': 'Delivery available at relay point',
    'product.securePayment': 'Secure payment',
    'product.returnPolicy': 'Returns within 14 days',
    'product.description': 'Description',
    'product.noDescription': 'No description available for this product.',
    'product.specifications': 'Specifications',
    'product.reviews_tab': 'Reviews',
    'product.relatedProducts': 'Similar products',
    'product.notFound': 'Product not found',
    'product.category': 'Category',
    'product.stock': 'Stock',
    'product.units': 'units',
    'product.reference': 'Reference',
    'product.dateAdded': 'Date added',
    'product.favorites': 'Favorites',
    'product.share': 'Share',
    'product.notSpecified': 'Not specified',
    'product.by': 'by',
    'product.seller': 'Seller',
    'product.reviewsComingSoon': 'Customer reviews will be available soon.',
    'product.addedToFavorites': 'Added to favorites',
    'product.removedFromFavorites': 'Removed from favorites',
    'product.favoriteError': 'Error managing favorites',
    'product.shareText': 'Check out this product on our marketplace',
    'product.linkCopied': 'Link copied to clipboard',
    'product.shareError': 'Error sharing product',
    'admin.cms.no_faq': 'No questions added yet. Click "Add Question" to get started.',
    'admin.translation_checker': 'Translation Checker',
    'admin.translation_checker_description': 'Automatically detects untranslated hardcoded strings in your code',
    'admin.scanning': 'Scanning...',
    'admin.start_scan': 'Start Scan',
    'admin.scan_complete': 'Scan Complete',
    'admin.issues_found': 'issues found',
    'admin.scan_error': 'Error during scan',
    'admin.files_scanned': 'Files Scanned',
    'admin.files_with_issues': 'Files with Issues',
    'admin.files_ok': 'Files OK',
    'admin.affected_files': 'Affected Files',
    'admin.issues_in_file': 'Issues in this file',
    'admin.all_issues': 'All Issues',

    // About Page
    'about.hero_title': 'About Us',
    'about.hero_description': 'Your trusted marketplace for secure local shopping',
    'about.mission_title': 'Our Mission',
    'about.values_title': 'Our Values',
    'about.security': 'Security',
    'about.security_description': 'Secure transactions and personal data protection',
    'about.community': 'Community',
    'about.community_description': 'Building a supportive and engaged community',
    'about.reliability': 'Reliability',
    'about.reliability_description': 'Quality service with a network of certified relay points',
    'about.innovation': 'Innovation',
    'about.innovation_description': 'Modern technologies for an optimal user experience',
    'about.active_vendors': 'Active Vendors',
    'about.satisfied_customers': 'Satisfied Customers',
    'about.relay_points': 'Relay Points',

    // How It Works Page
    'howItWorks.hero_title': 'How It Works',
    'howItWorks.hero_description': 'Discover how to use our platform with ease',
    'howItWorks.buyers_title': 'For Buyers',
    'howItWorks.sellers_title': 'For Sellers',
    'howItWorks.advantages_title': 'Our Advantages',
    'howItWorks.step1_title': 'Search for a product',
    'howItWorks.step1_description': 'Browse our catalog and find products that interest you',
    'howItWorks.step2_title': 'Choose a relay point',
    'howItWorks.step2_description': 'Select the pickup point closest to you',
    'howItWorks.step3_title': 'Pay online',
    'howItWorks.step3_description': 'Complete your payment securely via our platform',
    'howItWorks.step4_title': 'Collect your order',
    'howItWorks.step4_description': 'Show up at the relay point with your QR code to collect your products',
    'howItWorks.seller_step1_title': 'Create your shop',
    'howItWorks.seller_step1_description': 'Sign up and create your vendor space in just a few clicks',
    'howItWorks.seller_step2_title': 'Add your products',
    'howItWorks.seller_step2_description': 'Publish your items with photos and detailed descriptions',
    'howItWorks.seller_step3_title': 'Deliver to relay point',
    'howItWorks.seller_step3_description': 'Drop off orders at the nearest relay point',
    'howItWorks.advantage1_title': 'Maximum security',
    'howItWorks.advantage1_description': 'Your transactions are protected and your data secured',
    'howItWorks.advantage2_title': 'Payment flexibility',
    'howItWorks.advantage2_description': 'Multiple payment options available',
    'howItWorks.advantage3_title': 'Local support',
    'howItWorks.advantage3_description': 'Support the local economy by shopping near you',

    // Support Page
    'support.hero_title': 'Customer Support',
    'support.hero_description': 'We are here to help you',
    'support.contact_title': 'Contact Us',
    'support.contact_description': 'Multiple ways to reach us',
    'support.email': 'Email',
    'support.phone': 'Phone',
    'support.live_chat': 'Live chat',
    'support.form_title': 'Send us a message',
    'support.form_description': 'Fill out the form below',
    'support.faq_title': 'Frequently Asked Questions',
    'support.faq_description': 'Find quick answers',
    'support.hours_title': 'Opening Hours',
    'support.hours_description': 'We are available',
    'support.weekdays': 'Monday - Friday',
    'support.weekend': 'Saturday - Sunday',
    'support.form_name': 'Full name',
    'support.form_email': 'Email',
    'support.form_subject': 'Subject',
    'support.form_message': 'Message',
    'support.form_submit': 'Send',
    'support.form_sending': 'Sending...',
    'support.form_success': 'Message sent successfully',
    'support.form_success_description': 'We will respond to you as soon as possible',
    'support.form_error': 'Sending error',
    'support.name_label': 'Full Name',
    'support.name_placeholder': 'Your first and last name',
    'support.email_label': 'Email Address',
    'support.email_placeholder': 'your.email@example.com',
    'support.subject_label': 'Subject',
    'support.subject_placeholder': 'Subject of your message',
    'support.message_label': 'Message',
    'support.message_placeholder': 'Describe your request in detail...',
    'support.send': 'Send Message',
    'support.success_message': 'Message sent successfully! We will reply shortly.',
    'support.error_message': 'Error sending message. Please try again.',
    
    // Relay Points Page
    'relayPoints.hero_title': 'Relay Points',
    'relayPoints.hero_description': 'Find the nearest relay point',
    'relayPoints.search_placeholder': 'Search by name, address or municipality...',
    'relayPoints.how_it_works_title': 'How it works?',
    'relayPoints.how_it_works_description': 'Collect your orders in 4 simple steps',
    'relayPoints.no_results': 'No relay point found',
    'relayPoints.loading': 'Loading relay points...',
    'relayPoints.localize': 'Locate',
    'relayPoints.itinerary': 'Directions',
    'relayPoints.contact': 'Contact',
    'relayPoints.choose': 'Choose',
    'relayPoints.selected': 'Relay point selected',
    'admin.analytics.users': 'Users',
    'admin.analytics.vendors': 'Vendors',
    'admin.analytics.orders': 'Orders',
    'admin.analytics.revenue': 'Revenue',
    'admin.analytics.employees': 'Employees',

    // CMS Tabs
    'cms.tabs.dashboard': 'Dashboard',
    'cms.tabs.settings': 'Settings',
    'cms.tabs.categories': 'Categories',
    'cms.tabs.menus': 'Menus',
    'cms.tabs.footer': 'Footer',
    'cms.tabs.pages': 'Pages',

    // CMS Site Configuration
    'cms.site_configuration_title': 'Site configuration',
    'cms.site_configuration_subtitle': 'Full interface to customize your marketplace',
    'cms.tabs.site_content': 'Site content',
    'cms.tabs.system_settings': 'System settings',

    // CMS Footer Admin
    'cms.footer.sections_title': 'Footer sections',
    'cms.footer.sections_desc': 'Customize the sections displayed in your site footer',
    'cms.footer.new_section': 'New section',
    'cms.footer.no_sections': 'No footer sections',
    'cms.footer.create_first_section': 'Start by creating your first footer section',
    'cms.footer.edit_section': 'Edit footer section',
    'cms.footer.dialog_desc': 'Configure footer sections and links',
    'cms.footer.cancel': 'Cancel',
    'cms.footer.save_edit': 'Update',
    'cms.footer.save_create': 'Create',
    'cms.footer.links': 'links',
    'cms.footer.more': 'more link(s)',
    'cms.footer.untitled': 'Untitled',
    'cms.footer.confirm_delete': 'Are you sure you want to delete this section?',
     'cms.footer.updated_success': 'Footer section updated successfully',
     'cms.footer.created_success': 'Footer section created successfully',
     'cms.footer.deleted_success': 'Footer section deleted successfully',
     'cms.footer.section_title': 'Section title',
     'cms.footer.section_key': 'Section key',
     'cms.footer.section_links': 'Section links',
     'cms.footer.section_active': 'Active section',
     'cms.footer.title_placeholder': 'About',
     'cms.footer.key_placeholder': 'about',
     'cms.footer.link_title': 'Title',
     'cms.footer.link_url': 'URL',
     'cms.footer.link_icon': 'Icon (optional)',
     'cms.footer.link_title_placeholder': 'Our story',
     'cms.footer.link_url_placeholder': '/about',
     'cms.footer.add_link': 'Add link',
     'cms.footer.remove_link': 'Remove link',
     'cms.footer.delete_section': 'Delete section',
     'cms.footer.sort_order': 'Display order',

    // User Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Welcome',
    'dashboard.logout': 'Logout',
    'dashboard.total_orders': 'Total orders',
    'dashboard.pending_orders': 'Pending orders',
    'dashboard.completed_orders': 'Completed orders',
    'dashboard.my_orders_tab': 'My orders',
    'dashboard.payments_tab': 'Payments',
    'dashboard.disputes_tab': 'Disputes',
    'dashboard.messages_tab': 'Messages',
    'dashboard.account_settings_tab': 'Account settings',
    'dashboard.my_orders_title': 'My orders',
    'dashboard.my_orders_desc': 'Track your orders and their status',
    'dashboard.no_orders': 'You have no orders yet',
    'dashboard.discover_products': 'Discover products',
    'dashboard.pickupCode': 'Pickup code',
    'dashboard.presentCode': 'Present this code at the relay point to collect your order',
    'dashboard.expiresOn': 'Expires on',
    'dashboard.viewQRCode': 'View QR code',
    'dashboard.defaultRelay': 'Default relay point',
    'dashboard.selectRelayPreference': 'Select your preferred relay point for your future orders',
    'dashboard.relayPoint': 'Relay Point',
    'dashboard.register': 'Save',
    'dashboard.selectRelay': 'Select a relay point',
    'dashboard.saving': 'Saving...',
    'dashboard.selectRelayError': 'Please select a relay point',
    'dashboard.relayUpdated': 'Default relay point saved',
    'dashboard.updateError': 'Update error',

    // Order Status
    'order.status.pending_payment': 'Pending payment',
    'order.status.paid': 'Paid',
    'order.status.awaiting_drop_off': 'Awaiting drop-off',
    'order.status.available_for_pickup': 'Available for pickup',
    'order.status.picked_up': 'Picked up',
    'order.status.expired': 'Expired',
    'order.status.dispute': 'Dispute',
    'order.status.cancelled': 'Cancelled',

    'admin.employees.title': 'User Management',
    'admin.employees.subtitle': 'Manage user accounts and their permissions',
    'admin.users.export': 'Export',
    'admin.users.new_user': 'New user',
    'admin.config.categories.general': 'General',
    'admin.config.categories.general_desc': 'Site general settings',
    'admin.config.site_info': 'Site information',
    'admin.config.site_name': 'Site name',
    'admin.config.slogan': 'Slogan',
    'admin.config.slogan_desc': 'Short platform description',
    'admin.config.description': 'Description',
    'admin.config.description_desc': 'Detailed description for SEO',
    'admin.config.reset': 'Reset',
    'admin.config.save_all': 'Save all',
    'admin.config.advanced': 'Advanced configuration',
    'admin.config.advanced_desc': 'Customize all aspects of your platform',
    'admin.config.appearance': 'Appearance',
    'admin.config.appearance_desc': 'Visual customization',
    'admin.config.payments': 'Payments',
    'admin.config.payments_desc': 'Payment configuration',
    'admin.config.delivery': 'Delivery',
    'admin.config.delivery_desc': 'Delivery settings',
    'admin.config.notifications': 'Notifications',
    'admin.config.notifications_desc': 'Notification management',

    // HR Management
    'hr.total_employees': 'Total employees',
    'hr.active': 'Active',
    'hr.positions': 'Positions',
    'hr.departments': 'Departments',
    'hr.employees': 'Employees',
    'hr.leave': 'Leave',
    'hr.evaluations': 'Evaluations',
    'hr.training': 'Training',
    'hr.documents': 'Documents',
    'hr.employee_management': 'Employee management',
    'hr.employee_management_desc': 'Manage employee profiles and information',
    'hr.new_employee': 'New employee',
    'hr.employee': 'Employee',
    'hr.code': 'Code',
    'hr.position': 'Position',
    'hr.department': 'Department',
    'hr.status': 'Status',
    'hr.actions': 'Actions',
    'hr.modify': 'Modify',
    'employee': 'Employee',
    'code': 'Code',
    'position': 'Position',
    'department': 'Department',
    'account': 'Account',
    'employees.status': 'Status',
    'actions': 'Actions',
    'employees.active': 'Active',
    'employees.inactive': 'Inactive',
    'terminated': 'Terminated',
    'filtered': 'filtered',
    'employees.total': 'total',
    'no_employees_found': 'No employees found',
    'create_account': 'Create account',
    'create_user_account': 'Create user account',
    'link_user_account': 'Link user account',
    'link_account': 'Link account',
    'select_user': 'Select user',
    'select_user_placeholder': 'Choose a user...',
    'no_available_users': 'No available users for linking',
    'system_role': 'System role',
    'temporary_password': 'Temporary password',
    'enter_password': 'Enter password',
    'password_help': 'Employee must change this password on first login',
    'fill_required_fields': 'Please fill in all required fields',
    'employees.success': 'Success',

    // API Keys
    'admin.api_keys.tab': 'API Keys',
    'admin.api_keys.title': 'API Keys Management',
    'admin.api_keys.description': 'Manage external API access keys',

    // Relay Points
    'relay.points': 'Relay points',
    'relay.manage_network': 'Manage pickup network',
    'relay.new_point': 'New relay point',
    'relay.view_map': 'View map',
    'relay.point': 'Relay point',
    'relay.contact': 'Contact',
    'relay.address': 'Address',
    'relay.status': 'Status',
    'relay.actions': 'Actions',
    'relay.modify': 'Modify',

    // Home - Relay Section
    'relay.title': 'Relay points near you',
    'relay.description': 'Find a relay point to easily pick up your orders.',
    'relay.step1': 'Search',
    'relay.step1.desc': 'Enter your neighborhood or commune',
    'relay.step2': 'Choose',
    'relay.step2.desc': 'Select the closest point',
    'relay.step3': 'Confirm',
    'relay.step3.desc': 'Confirm during checkout',
    'relay.step4': 'Pick up',
    'relay.step4.desc': 'Show your pickup code',
    'relay.find_point': 'Find a relay point',

    // Orders
    'orders.back_home': 'Back to home',
    'orders.title': 'My Orders',
    'orders.subtitle': 'Track the status of all your orders',
    'orders.tabs.all': 'All',
    'orders.tabs.active': 'Active',
    'orders.tabs.completed': 'Completed',
    'orders.tabs.cancelled': 'Cancelled',
    'orders.loading': 'Loading...',
    'orders.order_number': 'Order',
    'orders.placed_on': 'Placed on',
    'orders.view_details': 'View details',
    'orders.pay_now': 'Pay now',
    'orders.buy_again': 'Buy again',
    'orders.paid': 'Paid',
    'orders.preparing': 'Preparing',
    'orders.ready_for_pickup': 'Ready for pickup',
    'orders.no_orders': 'No orders',
    'orders.no_orders_message': 'You haven\'t placed any orders yet.',
    'orders.start_shopping': 'Start shopping',
    'orders.filters.title': 'Filters, search and sort',
    'orders.filters.reset': 'Reset',
    'orders.filters.status': 'Status',
    'orders.filters.all_statuses': 'All statuses',
    'orders.filters.all': 'All statuses',
    'orders.filters.search': 'Search',
    'orders.filters.search_placeholder': 'Order #, client, vendor...',
    'orders.filters.vendor_search_placeholder': 'Order #, product, relay point...',
    'orders.filters.period': 'Period',
    'orders.filters.all_periods': 'All periods',
    'orders.filters.today': 'Today',
    'orders.filters.week': 'This week',
    'orders.filters.month': 'This month',
    'orders.filters.year': 'This year',
    'orders.filters.sort_by': 'Sort by',
    'orders.filters.sort_date': 'Creation date',
    'orders.filters.sort_amount': 'Amount',
    'orders.filters.sort_status': 'Status',
    'orders.filters.sort_number': 'Order #',
    'orders.status.PENDING_PAYMENT': 'Pending payment',
    'orders.status.PAID': 'Paid',
    'orders.status.PREPARING': 'Preparing',
    'orders.status.READY_FOR_PICKUP': 'Ready for pickup',
    'orders.status.PICKED_UP': 'Picked up',
    'orders.status.CANCELLED': 'Cancelled',
    'orders.status.EXPIRED': 'Expired',

    // Employee management
    'employee.management': 'User management',
    'employee.total_users': 'Total users',
    'employee.active': 'Active',
    'employee.suspended': 'Suspended',
    'employee.administrators': 'Administrators',
    'employee.user_list': 'User list',
    'employee.user': 'User',
    'employee.role': 'Role',
    'employee.status': 'Status',
    'employee.actions': 'Actions',
    'employee.no_name': 'No name',
    'employee.no_phone': 'No phone',
    'employee.client': 'Client',
    'employee.admin': 'Administrator',
    'employee.super_admin': 'Super Admin',
    'employee.vendor': 'Vendor',
    'employee.relay': 'Relay point',
    'employee.search_placeholder': 'Search for a user...',
    'employee.all_roles': 'All roles',
    'employee.all_status': 'All statuses',
    'employee.update': 'Update',
    'employee.preferences': 'Preferences',
    'employee.statistics': 'Statistics',
    'employee.last_connection': 'Last connection',
    'employee.commands': 'Orders',

    // Account Settings (Dashboard)
    'accountSettings.personalInfo': 'Personal information',
    'accountSettings.manageProfile': 'Manage your profile information',
    'accountSettings.fullName': 'Full name',
    'accountSettings.phone': 'Phone',
    'accountSettings.businessName': 'Business name',
    'accountSettings.notProvided': 'Not provided',
    'accountSettings.editInfo': 'Edit information',
    'accountSettings.businessNamePlaceholder': 'Ex: My shop',
    'accountSettings.saving': 'Saving...',
    'accountSettings.save': 'Save',
    'accountSettings.cancel': 'Cancel',
    'accountSettings.emailAddress': 'Email address',
    'accountSettings.modifyEmail': 'Modify your email address',
    'accountSettings.currentEmail': 'Current email',
    'accountSettings.changeEmail': 'Change email',
    'accountSettings.newEmail': 'New email',
    'accountSettings.important': 'Important',
    'accountSettings.emailChangeNote': 'You will receive a confirmation email to validate your new address.',
    'accountSettings.updating': 'Updating...',
    'accountSettings.updateEmail': 'Update email',
    'accountSettings.password': 'Password',
    'accountSettings.changePassword': 'Change your password',
    'accountSettings.newPassword': 'New password',
    'accountSettings.confirmPassword': 'Confirm password',
    'accountSettings.updatePassword': 'Update password',
    'accountSettings.profileSettings': 'Profile preferences',
    'accountSettings.customizeExperience': 'Customize your experience',
    'accountSettings.language': 'Language',
    'accountSettings.currency': 'Currency',

    // Footer
    'footer.description': 'Your trusted marketplace in Kinshasa',
    'footer.quick_links': 'Quick links',
    'footer.categories': 'Categories',
    'footer.contact': 'Contact',
    'footer.newsletter': 'Newsletter',
    'footer.about': 'About',
    'footer.how_it_works': 'How it works',
    'footer.become_vendor': 'Become vendor',
    'footer.relay_points': 'Relay points',
    'footer.support': 'Support',
    'footer.email_placeholder': 'Your email',
    'footer.copyright': '© Nazandoo. All rights reserved',
    'footer.terms': 'Terms of Service',
    'footer.privacy': 'Privacy Policy',
    'footer.legal': 'Legal Notice',
    'footer.newsletterTitle': 'Newsletter',
    'footer.emailPlaceholder': 'your@email.com',
    'footer.subscribeButton': 'Subscribe',
    'footer.subscribing': 'Subscribing...',
    'footer.invalidEmail': 'Invalid email',
    'footer.emailTooLong': 'Email too long',
    'footer.subscribeError': 'Subscription error',
    'footer.subscribeSuccess': 'Successfully subscribed! Check your mailbox.',

    // Payment Methods
    'payments.title': 'Payment methods',
    'payments.description': 'Manage your registered payment methods',
    'payments.add': 'Add',
    'payments.addTitle': 'Add payment method',
    'payments.addCardDescription': 'Register a new credit card',
    'payments.addMobileMoneyDescription': 'Add a new Mobile Money payment method',
    'payments.paymentType': 'Payment type',
    'payments.card': 'Credit card',
    'payments.mobileMoney': 'Mobile Money',
    'payments.provider': 'Mobile operator',
    'payments.chooseProvider': 'Choose your operator',
    'payments.orangeMoney': 'Orange Money',
    'payments.airtelMoney': 'Airtel Money',
    'payments.mpesa': 'Vodacom M-Pesa',
    'payments.africellMoney': 'Africell Money',
    'payments.phoneNumber': 'Phone number',
    'payments.save': 'Save',
    'payments.cancel': 'Cancel',
    'payments.default': 'Default',
    'payments.setAsDefault': 'Set as default',
    'payments.delete': 'Delete',
    'payments.noMethods': 'No payment method registered',
    'payments.addFirstMethod': 'Add your first payment method',
    'payments.errorLoading': 'Error loading payment methods',
    'payments.fillAllFields': 'Please fill all fields',
    'payments.invalidPhone': 'Invalid phone format',
    'payments.methodAdded': 'Payment method added',
    'payments.errorAdding': 'Error adding method',
    'payments.setAsDefaultSuccess': 'Payment method set as default',
    'payments.errorSetDefault': 'Error updating',
    'payments.methodDeleted': 'Payment method deleted',
    'payments.errorDeleting': 'Error deleting',

    // Disputes
    'disputes.title': 'My claims',
    'disputes.description': 'Track the status of your product claims',
    'disputes.noDisputes': 'No claims in progress',
    'disputes.order': 'Order',
    'disputes.details': 'Details',
    'disputes.detailsTitle': 'Claim details',
    'disputes.reason': 'Reason',
    'disputes.description_label': 'Description',
    'disputes.status': 'Status',
    'disputes.adminResponse': 'Administrator response',
    'disputes.createdAt': 'Created at',
    'disputes.resolvedAt': 'Resolved at',
    'disputes.defectiveProduct': 'Defective product',
    'disputes.wrongItem': 'Wrong item',
    'disputes.missingItem': 'Missing item',
    'disputes.damaged': 'Damaged product',
    'disputes.notAsDescribed': 'Not as described',
    'disputes.other': 'Other',
    'disputes.pending': 'Pending',
    'disputes.inProgress': 'In progress',
    'disputes.resolved': 'Resolved',
    'disputes.rejected': 'Rejected',
    'disputes.errorLoading': 'Error loading claims',

    // Messaging
    'messaging.title': 'Messaging',
    'messaging.description': 'Your conversations with vendors and administrators',
    'messaging.noConversations': 'No conversations',
    'messaging.unread': 'unread',
    'messaging.refreshing': 'Refreshing...',
    'messaging.pullToRefresh': 'Release to refresh',
    'messaging.loading': 'Loading...',
    'messaging.errorLoading': 'Error loading messages',
    'messaging.vendor': 'Vendor',

    // Orders
    'orders.recent': 'Recent orders',
    'orders.recent_desc': 'Track orders and their progress',
    'orders.order': 'Order',
    'orders.client': 'Client',
    'orders.vendor': 'Vendor',
    'orders.relay_point': 'Relay point',
    'orders.amount': 'Amount',
    'orders.unknown_product': 'Unknown product',
    'orders.unknown_client': 'Unknown client',
    'orders.unknown_vendor': 'Unknown vendor',
    'orders.unknown_relay': 'Unknown relay point',

    // Categories
    'categories.title': 'Explore our categories',
    'categories.description': 'Find exactly what you\'re looking for in our diverse categories',
    'categories.electronics': 'Electronics',
    'categories.fashion': 'Fashion',
    'categories.home': 'Home & Garden',
    'categories.kids': 'Kids & Baby',
    'categories.auto': 'Auto & Moto',
    'categories.games': 'Games & Toys',
    'categories.books': 'Books',
    'categories.health': 'Beauty & Health',
    'categories.sports': 'Sports & Leisure',
    'categories.automobile': 'Automobile',
    'categories.products_count': 'products',
    'categories.products_count_singular': 'product',

    // Search
    'search.title': 'Search Products',
    'search.button': 'Search',
    'search.results_for': 'Results for',
    'search.searching': 'Searching...',
    'search.result': 'result',
    'search.results': 'results',
    'search.sort_by': 'Sort by',
    'search.sort.relevance': 'Relevance',
    'search.sort.price_low': 'Price: Low to High',
    'search.sort.price_high': 'Price: High to Low',
    'search.sort.name': 'Name A-Z',
    'search.no_results': 'No results found',
    'search.try_different': 'Try different keywords',
    'search.browse_all': 'Browse all products',
    'search.start_searching': 'Start searching',
    'search.enter_keywords': 'Enter keywords to find products',
    'search.in': 'in',

    // Hero Section
    'hero.title': 'Kinshasa Marketplace',
    'hero.title.location': 'Your local marketplace',
    'hero.description': 'Discover the best local and international products directly in Kinshasa',
    'hero.explore_products': 'Explore products',
    'hero.become_vendor': 'Become vendor',
    'hero.fast_delivery': 'Fast delivery',
    'hero.fast_delivery.desc': '24-48h delivery',
    'hero.secure_payment': 'Secure payment',
    'hero.secure_payment.desc': 'Protected transactions',
    'hero.easy_pickup': 'Easy pickup',
    'hero.easy_pickup.desc': 'Convenient relay points',
    'hero.secure_order': 'Secure order',
    'hero.satisfied_customers': 'Satisfied customers',
    'hero.clients_satisfied_label': 'satisfied customers',

    // Vendors
    'vendor.manage_vendors': 'Manage vendor requests and status',
    'vendor.applications': 'Vendor applications',
    'vendor.review_applications': 'Review and process new vendor registration requests',
    'applications': 'Applications',

    // Vendor Account translations
    'vendor.title': 'Vendor Space',
    'vendor.subtitle': 'Manage your shop and products',
    'vendor.refresh': 'Refresh',
    'vendorAccount.loading': 'Loading your vendor space...',
    'vendorAccount.refreshing': 'Refreshing...',
    'vendorAccount.products': 'Products',
    'vendorAccount.orders': 'Orders',
    'vendorAccount.revenue': 'Revenue',
    'vendorAccount.lowStock': 'Low Stock',
    'vendorAccount.activeProducts': 'active',
    'vendorAccount.toProcess': 'to process',
    'vendorAccount.totalSales': 'Total sales',
    'vendorAccount.productsLowStock': 'products out of stock',
    'vendorAccount.stockTotal': 'Total stock:',
    'vendorAccount.units': 'units',
    'vendorAccount.lowStockAlert': 'products with low stock need your attention',
    'vendorAccount.pendingDropAlert': 'orders awaiting drop-off',
    'vendorAccount.stock': 'Stock',
    'vendorAccount.team': 'Team',
    'vendorAccount.settings': 'Settings',
    'vendorAccount.analytics': 'Analytics',
    'vendor.product_catalog': 'Product catalog',
    'vendor.manage_inventory': 'Manage your inventory and stock',
    'vendor.new_product': 'New product',
    'vendor.filters.title': 'Filters, search and sort',
    'vendor.filters.reset': 'Reset',
    'vendor.filters.status': 'Status',
    'vendor.filters.allStatuses': 'All statuses',
    'vendor.filters.search': 'Search',
    'vendor.filters.searchPlaceholder': 'Product #, title, category...',
    'vendor.filters.period': 'Period',
    'vendor.filters.allPeriods': 'All periods',
    'vendor.filters.today': 'Today',
    'vendor.filters.week': 'This week',
    'vendor.filters.month': 'This month',
    'vendor.filters.year': 'This year',
    'vendor.filters.sortBy': 'Sort by',
    'vendor.filters.createdAt': 'Creation date',
    'vendor.filters.name': 'Name',
    'vendor.filters.price': 'Price',
    'vendor.filters.stock': 'Stock',
    'vendor.filters.resultsFound': 'products found',
    'vendor.filters.resultFound': 'product found',
    'product.product': 'Product',
    'product.price_label': 'Price',
    'product.status': 'Status',
    'product.actions': 'Actions',
    'product.lastUpdate': 'Last update',
    'product.active': 'Active',
    'product.inactive': 'Inactive',
    'product.noCatalog': 'No products in your catalog',
    'product.addFirst': 'Add your first product to get started',
    'product.edit': 'Edit product',
    'product.new': 'New product',
    'product.formDesc': 'Fill in your product information',
    'product.name': 'Product name',
    'product.price': 'Price',
    'product.currency': 'Currency',
    'product.selectCurrency': 'Select currency',
    'product.selectCategory': 'Select a category',
    'product.cancel': 'Cancel',
    'product.modify': 'Modify',
    'product.create': 'Create',
    'product.multilingualInfo': 'Multilingual information',
    'product.translating': 'Translating...',
    'product.autoTranslate': 'Auto-translate',
    'product.translationSuccessMessage': 'Translations generated successfully! Review and adjust if needed.',
    'product.translationHint': 'Fill in the title in your language, then click "Auto-translate" to generate the other languages. You can then edit them manually.',
    'product.sourceLanguage': 'Source language',
    'product.namePlaceholder': 'Product name',
    'product.descriptionPlaceholder': 'Detailed product description',
    'product.translationComplete': 'Complete translation',
    'product.translationPartial': 'Partial translation',
    'orders.track': 'Track and manage your orders',
    'orders.noOrders': 'No orders at the moment',
    'orders.product': 'Product',
    'orders.pickupPoint': 'Pickup point',
    'orders.pickedUp': 'Picked up',
    'orders.awaitingDropOff': 'Awaiting drop-off',
    'stock.title': 'Stock management',
    'stock.description': 'Overview of your stock. Total:',
    'stock.units': 'units',
    'stock.lowStock': 'Low stock (≤5)',
    'stock.noLowStock': 'No products with low stock',
    'stock.stockByCategory': 'Stock by category',
    'stock.products': 'products',
    'stock.stockDetails': 'Stock details',
    'stock.currentStock': 'Current stock',
    'stock.state': 'State',
    'stock.critical': 'Critical',
    'stock.low': 'Low',
    'stock.good': 'Good',
    'team.title': 'Team management',
    'team.description': 'Manage your store managers',
    'team.addManager': 'Add manager',
    'team.noManagers': 'No managers assigned',
    'team.addFromEmployees': 'Add managers from your employees',
    'team.addManagerDialog': 'Add a manager',
    'team.selectEmployee': 'Select an employee to promote as manager',
    'team.employee': 'Employee',
    'team.chooseEmployee': 'Choose an employee',
    'team.cancel': 'Cancel',
    'team.add': 'Add',
    'settings.title': 'Store settings',
    'settings.description': 'Configure your store information',
    'settings.shopName': 'Shop name',
    'settings.shopNamePlaceholder': 'Ex: Central Store',
    'settings.contactEmail': 'Contact email',
    'settings.contactEmailPlaceholder': 'contact@mystore.cd',
    'settings.businessPhone': 'Shop phone number',
    'settings.businessPhonePlaceholder': 'Ex: +243812345678',
    'settings.mainAddress': 'Main address',
    'settings.mainAddressPlaceholder': 'Full address of your store',
    'settings.businessHours': 'Business hours',
    'settings.monday': 'Monday',
    'settings.tuesday': 'Tuesday',
    'settings.wednesday': 'Wednesday',
    'settings.thursday': 'Thursday',
    'settings.friday': 'Friday',
    'settings.saturday': 'Saturday',
    'settings.sunday': 'Sunday',
    'settings.open': 'Open',
    'settings.opening': 'Opening',
    'settings.closing': 'Closing',
    'settings.closed': 'Closed',
    'settings.saveInfo': 'Save information',
      'settings.withdrawalDefaults': 'Withdrawal default settings',
      'settings.withdrawalDefaultsDesc': 'Configure your details for withdrawals (mobile money or bank transfer)',
      'settings.mobileNumber': 'Mobile phone number',
      'settings.mobileNumberPlaceholder': 'Ex: +243812345678',
      'settings.mobileProvider': 'Mobile payment provider',
      'settings.selectMobileProvider': 'Select a provider',
      'settings.bankAccount': 'Bank account number',
      'settings.bankAccountPlaceholder': 'Ex: 1234567890',
      'settings.bankName': 'Bank name',
      'settings.bankNamePlaceholder': 'Ex: BCDC, Equity Bank, etc.',
    'analytics.description': 'Track your store performance',
    'analytics.totalRevenue': 'Total revenue',
    'analytics.sinceBeginning': 'Since beginning',
    'analytics.ordersByStatus': 'Orders by status',
    'status.pending_payment': 'Pending payment',
    'status.paid': 'Paid',
    'status.awaiting_drop_off': 'Awaiting drop-off',
    'status.available_for_pickup': 'Available for pickup',
    'status.picked_up': 'Picked up',
    'status.expired': 'Expired',
    'status.dispute': 'Dispute',
    'currency.cdf_full': 'Congolese Franc (CDF)',
    'currency.usd_full': 'US Dollar (USD)',
    'currency.eur_full': 'Euro (EUR)',
    'category.electronique': 'Electronics',
    'category.mode': 'Fashion',
    'category.maison': 'Home & Garden',
    'category.sport': 'Sports & Leisure',
    'category.livre': 'Books',
    'category.beaute': 'Beauty & Health',
    'vendorAccount.dataUpdated': 'Data updated',
    'vendorAccount.dataUpdatedDesc': 'All your data has been updated',
    'vendorAccount.loadingError': 'Loading error',
    'vendorAccount.loadingErrorDesc': 'Unable to load data',
    'vendorAccount.productUpdated': 'Product updated',
    'vendorAccount.productCreated': 'Product created',
    'vendorAccount.error': 'Error',
    'vendorAccount.confirmDelete': 'Are you sure you want to delete this product?',
    'vendorAccount.productDeleted': 'Product deleted',
    'unauthorizedAccess': 'Unauthorized access',
    'unauthorizedDesc': 'You don\'t have the necessary permissions to access this space',
    'none': 'None',
    'unspecified': 'Unspecified',
    'write_comment': 'Write a comment',

    // Products Section
    'products.title': 'Products',
    'products.description': 'Discover our most popular items',
    'products.no_products': 'No products available',
    'products.view_all': 'View all products',
    'analytics.title': 'Analytics',
    'managers': 'Managers',
    
    // Vendor sections - Stock Management
    'vendor.stock.management': 'Stock Management',
    'vendor.stock.overview.totalProducts': 'Total Products',
    'vendor.stock.overview.lowStock': 'Low Stock',
    'vendor.stock.overview.outOfStock': 'Out of Stock',
    'vendor.stock.overview.stockValue': 'Stock Value',
    'vendor.stock.lowStockLabel': 'Low stock (≤5)',
    'vendor.stock.noLowStock': 'No products with low stock',
    'vendor.stock.stockByCategory': 'Stock by category',
    'vendor.stock.units': 'units',
    'vendor.stock.alerts.outOfStockProducts': 'Out of stock products',
    'vendor.stock.alerts.lowStockProducts': 'Low stock (≤ 5 units)',
    'vendor.stock.actions.restock': 'Restock',
    'vendor.stock.actions.adjust': 'Adjust',
    'vendor.stock.inventory.title': 'Product Inventory',
    'vendor.stock.inventory.description': 'Manage your product stock levels',
    'vendor.stock.table.product': 'Product',
    'vendor.stock.table.category': 'Category',
    'vendor.stock.table.currentStock': 'Current Stock',
    'vendor.stock.table.unitValue': 'Unit Value',
    'vendor.stock.table.totalValue': 'Total Value',
    'vendor.stock.table.actions': 'Actions',
    'vendor.stock.uncategorized': 'Uncategorized',
    'vendor.stock.dialog.title': 'Adjust Stock',
    'vendor.stock.dialog.adjustmentType': 'Adjustment Type',
    'vendor.stock.dialog.stockIn': 'Stock In (+)',
    'vendor.stock.dialog.stockOut': 'Stock Out (-)',
    'vendor.stock.dialog.adjustment': 'Adjustment',
    'vendor.stock.dialog.quantity': 'Quantity',
    'vendor.stock.dialog.newStock': 'New Stock',
    'vendor.stock.dialog.reason': 'Reason (optional)',
    'vendor.stock.dialog.reasonPlaceholder': 'Ex: Restock, customer return, correction...',
    'vendor.stock.dialog.apply': 'Apply Adjustment',
    'vendor.stock.success.updated': 'Stock Updated',
    'vendor.stock.error.loading': 'Error loading data',
    'vendor.stock.error.loadingProducts': 'Error loading products',
    'vendor.stock.error.loadingTransactions': 'Error loading transactions',
    'vendor.stock.error.invalidQuantity': 'Please enter a valid quantity.'
  },
  'lg-fr': {
    'home': 'Ndako',
    'products': 'Biloko',
    'categories': 'Batánga',
    'login': 'Kokota',
    'logout': 'Kobima',
    'register': 'Komikoma',
    'dashboard': 'Mokanda',
    'admin': 'Moyangeli',
    'settings': 'Bongwana',
    'profile': 'Mokanda ya moto',
    'orders': 'Bakilato',
    'cart': 'Saki',
    'search': 'Boluka',
    'loading': 'Kozela...',
    'error': 'Libunga',
    'success': 'Elongi',
    'cancel': 'Kotika',
    'confirm': 'Kondima',
    'save': 'Kobomba',
    'edit': 'Kobongola',
    'delete': 'Kolongola',
    'add': 'Kobakisa',
    'create': 'Kosala',
    'update': 'Kosika',
    'view': 'Komona',
    'close': 'Kokanga',
    'open': 'Kofungola',
    'yes': 'Ee',
    'no': 'Te',
    'language': 'Lokota',
    'currency': 'Mbongo',

    // Header
    'header.hello': 'Mbote',
    'header.menu': 'Menu',
    'header.home': 'Ndako',
    'header.all_products': 'Biloko nyonso',
    'header.your_orders': 'Ba commande na yo',
    'header.your_account': 'Compte na yo',
    'header.vendor_account': 'Compte ya moteki',
    'header.admin_panel': 'Boyangeli',
    'header.points': 'Bisika ya kozongela',
    'header.deals': 'Ba promo',
    'header.buyer_account': 'Compte ya mosumueli',
    'header.my_profile': 'Profil na ngai',
    'header.my_orders': 'Ba commande na ngai',
    'header.vendor_dashboard': 'Tableau ya mokanda',
    'header.admin_account': 'Compte ya moyangeli',
    'header.administration': 'Boyangeli',
    'header.become_vendor': 'Kokóma moteki',
    'header.relay_management': 'Bobongisi ya bisika ya kozongela',
    'header.create_business': 'Kosala business na ngai',
    'header.cart': 'Saki',
    'header.logout': 'Kobima',
    'header.login': 'Kokota',
    'header.search.placeholder': 'Luka eloko...',

    // Makomi ya site
    'site.tagline': 'Esika na bino ya koteka',
    'site.description': 'Talá biloko ya malamu ya awa na ya mikili mosusu na Kinshasa',

    // Auth Page
    'auth.back_to_home': 'Zonga na ebandeli',
    'auth.access_account': 'Kɔta na compte na yo',
    'auth.connect_or_create': 'Kɔta to sala compte ya sika',
    'auth.signin': 'Kokɔta',
    'auth.signup': 'Komikoma',
    'auth.email': 'Email',
    'auth.password': 'Mot de passe',
    'auth.full_name': 'Nkombo mobimba',
    'auth.email_placeholder': 'email@na.yo',
    'auth.password_placeholder': '••••••••',
    'auth.full_name_placeholder': 'Jean Mukendi',
    'auth.signin_button': 'Kɔta',
    'auth.signup_button': 'Mikoma',
    'auth.signin_loading': 'Kokɔta...',
    'auth.signup_loading': 'Komikoma...',
    'auth.forgot_password': 'Obosani mot de passe?',
    'auth.forgot_password_title': 'Obosani Mot de Passe',
    'auth.forgot_password_subtitle': 'Tyá email na yo mpe tokotinda yo lien pona kobongola mot de passe',
    'auth.send_reset_link': 'Tinda lien',
    'auth.sending': 'Kotinda...',
    'auth.back_to_login': 'Zonga na connexion',
    'auth.forgot_password_success_title': 'Email etindami!',
    'auth.forgot_password_success_desc': 'Talá na boîte email na yo pona kobongola mot de passe',
    'auth.forgot_password_error_title': 'Libunga',
    'auth.forgot_password_error_email': 'Tyá email oyo ezali malamu',
    'auth.forgot_password_error_general': 'Libunga esalemi. Meka lisusu.',
    'auth.forgot_password_email_sent': 'Email ya kobongola etindami na adresse na yo',
    'auth.forgot_password_check_spam': 'Soki ozwi te, talá na dossier spam',
    'auth.send_another_email': 'Tinda email mosusu',
    'auth.forgot_password_help': 'Ozali na mposa ya lisalisi? Benga biso na',
    'auth.terms_accept': 'Na kokɔta, ondimi',
    'auth.terms_link': 'mibeko ya kosalela',
    'auth.privacy_link': 'politique ya kobatela ba données',
    'auth.and': 'na',
    'auth.biometric_login': 'Kokɔta na biométrie',
    'auth.use_biometric': 'Salelá {type}',
    'auth.biometric_faceid': 'Face ID',
    'auth.biometric_touchid': 'Touch ID',
    'auth.biometric_fingerprint': 'Empreinte digitale',
    'auth.biometric_authenticate': 'Ndima mpo okɔta',
    'auth.biometric_setup': 'Bongisa kokɔta na biométrie',
    'auth.biometric_enabled': 'Kokɔta na biométrie ezali kosala',
    'auth.biometric_error': 'Libunga ya ndimisi biométrie',

    // Admin
    'admin.title': 'Boyangeli',
    'admin.subtitle': 'Mokanda ya boyangeli',
    'admin.system_operational': 'Système ezali kosala',
    'admin.tabs.dashboard': 'Mokanda',
    'admin.tabs.users': 'Basaleli',
    'admin.tabs.vendors': 'Bateki',
    'admin.tabs.employees': 'Basali',
    'admin.tabs.relays': 'Bisika ya kozongela',
    'admin.tabs.orders': 'Ba commande',
    'admin.tabs.cms': 'CMS',
    'admin.tabs.config': 'Configuration',
    'admin.tabs.roles': 'Misala',
    'admin.tabs.activity': 'Misala oyo esalemi',
    'admin.tabs.bulk': 'Misala ya liboke',
    'admin.quick_actions': 'Misala ya nokinoki',
    'admin.new_category': 'Catégorie ya sika',
    'admin.new_page': 'Page ya sika',
    'admin.new_footer_section': 'Section footer ya sika',
    'admin.cms.loading': 'Ezali kokita...',
    'admin.cms.editor': 'Éditeur',
    'admin.cms.preview': 'Komona liboso',
    'admin.cms.save': 'Kobomba',
    'admin.cms.hero_tab': 'Hero',
    'admin.cms.sections_tab': 'Ba sections',
    'admin.cms.faq_tab': 'FAQ',
    'admin.cms.hero_title': 'Titre Hero',
    'admin.cms.hero_description': 'Description Hero',
    'admin.cms.hero_title_placeholder': 'Titre ya lokumu ya page',
    'admin.cms.hero_description_placeholder': 'Description na se ya titre',
    'admin.cms.section_title': 'Titre ya section',
    'admin.cms.section_content': 'Contenu',
    'admin.cms.section_title_placeholder': 'Titre ya section',
    'admin.cms.section_content_placeholder': 'Contenu ya section',
    'admin.cms.faq_title': 'Mituna oyo basololaka mingi',
    'admin.cms.add_faq': 'Kobakisa mituna',
    'admin.cms.question': 'Motuna',
    'admin.cms.answer': 'Eyano',
    'admin.cms.question_placeholder': 'Kotisa motuna',
    'admin.cms.answer_placeholder': 'Kotisa eyano',
    'admin.cms.saved': 'Contenu ebombami',
    'admin.cms.saved_description': 'Mbongwana ebombami malamu',
    'admin.cms.error': 'Libunga',
    'admin.cms.no_faq': 'Mituna moko te ezali. Kanda na "Kobakisa mituna" po obanda.',
    'admin.translation_checker': 'Motali ya traduction',
    'admin.translation_checker_description': 'Ezali kotalaka pe komonisa maloba oyo ezali te na traduction na code',
    'admin.scanning': 'Azali kotalaka...',
    'admin.start_scan': 'Bandá kotala',
    'admin.scan_complete': 'Kotala esili',
    'admin.issues_found': 'mikakatano ezwami',
    'admin.scan_error': 'Libunga na tango ya kotala',
    'admin.files_scanned': 'Ba fichier etalami',
    'admin.files_with_issues': 'Ba fichier na mikakatano',
    'admin.files_ok': 'Ba fichier malamu',
    'admin.affected_files': 'Ba fichier oyo ezwami',
    'admin.issues_in_file': 'Mikakatano na fichier oyo',
    'admin.all_issues': 'Mikakatano nionso',

    // About Page
    'about.hero_title': 'Ntina na biso',
    'about.hero_description': 'Esika na bino ya koteka ya malamu mpe ya libateli',
    'about.mission_title': 'Mokano na biso',
    'about.values_title': 'Motuya na biso',
    'about.security': 'Libateli',
    'about.security_description': 'Ba transaction ya libateli mpe kobatela ba données personnelles',
    'about.community': 'Lisanga',
    'about.community_description': 'Kotia na esika lisanga ya bolendi mpe ya komipesa',
    'about.reliability': 'Boyengebene',
    'about.reliability_description': 'Service ya malamu na réseau ya ba points relais oyo ezwi',
    'about.innovation': 'Innovation',
    'about.innovation_description': 'Ba technologies ya sika mpo na expérience ya mosaleli ya malamu',
    'about.active_vendors': 'Bateki ya kosala',
    'about.satisfied_customers': 'Bakiliya ya esengo',
    'about.relay_points': 'Bisika ya kozongela',

    // How It Works Page
    'howItWorks.hero_title': 'Ndenge ezali kosala',
    'howItWorks.hero_description': 'Koyeba kosalela plateforme na biso na pete',
    'howItWorks.buyers_title': 'Mpo na basombi',
    'howItWorks.sellers_title': 'Mpo na bateki',
    'howItWorks.advantages_title': 'Bolamu na biso',
    'howItWorks.step1_title': 'Luka eloko',
    'howItWorks.step1_description': 'Talá kataloge na biso mpe zwa biloko oyo esepelisi yo',
    'howItWorks.step2_title': 'Pona esika ya kozongela',
    'howItWorks.step2_description': 'Pona esika ya kozonga oyo ezali pene na yo',
    'howItWorks.step3_title': 'Futa na internet',
    'howItWorks.step3_description': 'Futa na libateli na nzela ya plateforme na biso',
    'howItWorks.step4_title': 'Zonga commande na yo',
    'howItWorks.step4_description': 'Kende na esika ya kozongela na code QR na yo mpo na kozwa biloko na yo',
    'howItWorks.seller_step1_title': 'Sala magazini na yo',
    'howItWorks.seller_step1_description': 'Mikoma mpe sala esika na yo ya moteki na click moke',
    'howItWorks.seller_step2_title': 'Bakisa biloko na yo',
    'howItWorks.seller_step2_description': 'Bimisa biloko na yo na ba photo mpe ba description ya malamu',
    'howItWorks.seller_step3_title': 'Kende na esika ya kozongela',
    'howItWorks.seller_step3_description': 'Tia ba commande na esika ya kozongela oyo ezali pene',
    'howItWorks.advantage1_title': 'Libateli ya mingi',
    'howItWorks.advantage1_description': 'Ba transaction na yo ezali kobatelama mpe ba données na yo ezali na libateli',
    'howItWorks.advantage2_title': 'Flexibilité ya kofuta',
    'howItWorks.advantage2_description': 'Ba options mingi ya kofuta ezali',
    'howItWorks.advantage3_title': 'Lisungi ya awa',
    'howItWorks.advantage3_description': 'Sunga économie locale na kosomba pene na yo',

    // Support Page
    'support.hero_title': 'Lisungi ya kiliya',
    'support.hero_description': 'Tozali awa mpo na kosunga yo',
    'support.contact_title': 'Benga biso',
    'support.contact_description': 'Ba moyens mingi ya kokutana na biso',
    'support.email': 'Email',
    'support.phone': 'Telefone',
    'support.live_chat': 'Chat ya sikoyo',
    'support.form_title': 'Tinda biso message',
    'support.form_description': 'Tondisa formulaire oyo ezali awa',
    'support.faq_title': 'Mituna oyo ebongamaka mingi',
    'support.faq_description': 'Zwa biyano nokinoki',
    'support.hours_title': 'Ngonga ya kofungola',
    'support.hours_description': 'Tozali',
    'support.weekdays': 'Lundi - Vendredi',
    'support.weekend': 'Samedi - Dimanche',
    'support.form_name': 'Kombo mobimba',
    'support.form_email': 'Email',
    'support.form_subject': 'Sujet',
    'support.form_message': 'Message',
    'support.form_submit': 'Tinda',
    'support.form_sending': 'Kotinda...',
    'support.form_success': 'Message etindami malamu',
    'support.form_success_description': 'Toko yanola yo nokinoki',
    'support.form_error': 'Libunga ya kotinda',
    
    // Relay Points Page
    'relayPoints.hero_title': 'Bisika ya kozongela',
    'relayPoints.hero_description': 'Kuta esika ya kozongela oyo ezali pene',
    'relayPoints.search_placeholder': 'Luka na kombo, adresse to commune...',
    'relayPoints.how_it_works_title': 'Ndenge ezali kosala?',
    'relayPoints.how_it_works_description': 'Zonga ba commande na yo na ba étapes 4 ya pete',
    'relayPoints.no_results': 'Esika ya kozongela ezwami te',
    'relayPoints.loading': 'Kozela bisika ya kozongela...',
    'relayPoints.localize': 'Kotala esika',
    'relayPoints.itinerary': 'Nzela',
    'relayPoints.contact': 'Benga',
    'relayPoints.choose': 'Pona',
    'relayPoints.selected': 'Esika ya kozongela eponami',
    'admin.analytics.users': 'Basaleli',
    'admin.analytics.vendors': 'Bateki',
    'admin.analytics.orders': 'Ba commande',
    'admin.analytics.revenue': 'Mbongo ya litomba',
    'admin.analytics.employees': 'Basali',

    // CMS Tabs
    'cms.tabs.dashboard': 'Mokanda',
    'cms.tabs.settings': 'Ba paramètres',
    'cms.tabs.categories': 'Ba catégorie',
    'cms.tabs.menus': 'Ba menu',
    'cms.tabs.footer': 'Pied ya page',
    'cms.tabs.pages': 'Ba page',

    // User Dashboard
    'dashboard.title': 'Mokanda',
    'dashboard.welcome': 'Boyei bolamu',
    'dashboard.logout': 'Kobima',
    'dashboard.total_orders': 'Ba commande nyonso',
    'dashboard.pending_orders': 'Ba commande oyo ezali kozela',
    'dashboard.completed_orders': 'Ba commande esilá',
    'dashboard.my_orders_tab': 'Ba commande na ngai',
    'dashboard.payments_tab': 'Ba paiement',
    'dashboard.disputes_tab': 'Ba plainte',
    'dashboard.messages_tab': 'Ba message',
    'dashboard.account_settings_tab': 'Ba paramètres ya compte',
    'dashboard.my_orders_title': 'Ba commande na ngai',
    'dashboard.my_orders_desc': 'Landa etat ya ba commande na yo',
    'dashboard.no_orders': 'Ozali nanu na commande te',
    'dashboard.discover_products': 'Kuta biloko',
    'dashboard.myOrders': 'Ba commande na ngai',
    'dashboard.payments': 'Ba paiement',
    'dashboard.disputes': 'Ba plainte',
    'dashboard.messages': 'Ba message',
    'dashboard.accountSettings': 'Ba paramètres ya compte',
    'dashboard.defaultRelay': 'Esika ya kozongela ya liboso',
    'dashboard.selectRelayPreference': 'Pona esika ya kozongela oyo olingi mpo na ba commande na yo ya sika',
    'dashboard.relayPoint': 'Esika ya kozongela',
    'dashboard.register': 'Kobomba',
    'dashboard.selectRelay': 'Pona esika ya kozongela',
    'dashboard.saving': 'Kobomba...',
    'dashboard.selectRelayError': 'Pona esika ya kozongela',
    'dashboard.relayUpdated': 'Esika ya kozongela ya liboso ebombami',
    'dashboard.updateError': 'Libunga ntango ya kobomba',
    'dashboard.pickupCode': 'Code ya kozwa',
    'dashboard.presentCode': 'Lakisa code oyo na esika ya kozongela mpo na kozwa commande na yo',
    'dashboard.expiresOn': 'Ekosila',
    'dashboard.viewQRCode': 'Kotala code QR',
    'dashboard.viewDetails': 'Kotala makambo',
    'dashboard.cancelOrder': 'Kotya commande pembeni',
    'dashboard.cancelOrderTitle': 'Kotya commande pembeni?',
    'dashboard.cancelOrderConfirm': 'Olingi solo kotya commande pembeni',
    'dashboard.cancelOrderWarning': 'Likambo oyo ekokoma définitif mpe ekoki kobongola te.',
    'dashboard.back': 'Kozonga',
    'dashboard.confirmCancel': 'Kondima kotya pembeni',

    // Account Settings (Dashboard)
    'accountSettings.personalInfo': 'Makambo ya moto',
    'accountSettings.manageProfile': 'Yangela makambo ya profil na yo',
    'accountSettings.fullName': 'Kombo mobimba',
    'accountSettings.phone': 'Telefone',
    'accountSettings.businessName': 'Kombo ya mombongo',
    'accountSettings.notProvided': 'Ezali te',
    'accountSettings.editInfo': 'Kobongisa makambo',
    'accountSettings.businessNamePlaceholder': 'Ndakisa: Magazini na ngai',
    'accountSettings.saving': 'Kobomba...',
    'accountSettings.save': 'Kobomba',
    'accountSettings.cancel': 'Kotika',
    'accountSettings.emailAddress': 'Adresse email',
    'accountSettings.modifyEmail': 'Kobongisa email na yo',
    'accountSettings.currentEmail': 'Email ya lelo',
    'accountSettings.changeEmail': 'Kobongola email',
    'accountSettings.newEmail': 'Email ya sika',
    'accountSettings.important': 'Ya ntina',
    'accountSettings.emailChangeNote': 'Okozwa email ya kondima mpo na ko valider adresse na yo ya sika.',
    'accountSettings.updating': 'Kobongisa...',
    'accountSettings.updateEmail': 'Kobongisa email',
    'accountSettings.password': 'Mot de passe',
    'accountSettings.changePassword': 'Kobongola mot de passe na yo',
    'accountSettings.newPassword': 'Mot de passe ya sika',
    'accountSettings.confirmPassword': 'Kondima mot de passe',
    'accountSettings.updatePassword': 'Kobongisa mot de passe',
    'accountSettings.profileSettings': 'Ba préférence ya profil',
    'accountSettings.customizeExperience': 'Salá makambo na ndenge olingi',
    'accountSettings.language': 'Lokota',
    'accountSettings.currency': 'Mbongo',

      // Store Settings
      'settings.withdrawalDefaults': 'Paramètres ya liboso ya bozwi mbongo',
      'settings.withdrawalDefaultsDesc': 'Tinda ndenge ya koyeba mpo na bozwi mbongo (mobile money to transfert bancaire)',
      'settings.mobileNumber': 'Nimero ya telefone ya mobile',
      'settings.mobileNumberPlaceholder': 'Ndakisa: +243812345678',
      'settings.mobileProvider': 'Fournisseur ya paiement mobile',
      'settings.selectMobileProvider': 'Pona fournisseur moko',
      'settings.bankAccount': 'Nimero ya compte bancaire',
      'settings.bankAccountPlaceholder': 'Ndakisa: 1234567890',
      'settings.bankName': 'Kombo ya banque',
      'settings.bankNamePlaceholder': 'Ndakisa: BCDC, Equity Bank, etc.',

    // Order Status
    'order.status.pending_payment': 'Ezali kozela paiement',
    'order.status.paid': 'Efutami',
    'order.status.awaiting_drop_off': 'Ezali kozela kotia',
    'order.status.available_for_pickup': 'Ebongi pona kozwa',
    'order.status.picked_up': 'Ezwami',
    'order.status.expired': 'Esilá ntango',
    'order.status.dispute': 'Matatá',
    'order.status.cancelled': 'Etyelemi',

    'admin.employees.title': 'Boyangeli ya basaleli',
    'admin.employees.subtitle': 'Yangela ba compte ya basaleli mpe ba ndingisa na bango',
    'admin.users.export': 'Kobimisa',
    'admin.users.new_user': 'Mosaleli ya sika',
    'admin.config.categories.general': 'Général',
    'admin.config.categories.general_desc': 'Ba paramètre ya site ya ntina',
    'admin.config.site_info': 'Makambo ya site',
    'admin.config.site_name': 'Kombo ya site',
    'admin.config.slogan': 'Slogan',
    'admin.config.slogan_desc': 'Ndimbola mokuse ya plateforme na yo',
    'admin.config.description': 'Ndimbola',
    'admin.config.description_desc': 'Ndimbola ya makambo mingi mpo na SEO',
    'admin.config.reset': 'Kozongisa na ebandeli',
    'admin.config.save_all': 'Kobomba nyonso',
    'admin.config.advanced': 'Configuration avancée',
    'admin.config.advanced_desc': 'Salá makambo nyonso ya plateforme na yo ndenge olingi',
    'admin.config.categories': 'Ba catégorie',
    'admin.config.appearance': 'Lolenge',
    'admin.config.appearance_desc': 'Bosalisi ya biloko oyo emonani',
    'admin.config.payments': 'Bafuteli',
    'admin.config.payments_desc': 'Configuration ya bafuteli',
    'admin.config.delivery': 'Livraison',
    'admin.config.delivery_desc': 'Ba paramètre ya livraison',
    'admin.config.notifications': 'Ba notification',
    'admin.config.notifications_desc': 'Boyangeli ya ba notification',

    // HR Management
    'hr.total_employees': 'Basali nyonso',
    'hr.active': 'Oyo bazali kosala',
    'hr.positions': 'Bisika ya mosala',
    'hr.departments': 'Ba département',
    'hr.employees': 'Basali',
    'hr.leave': 'Bopemi',
    'hr.evaluations': 'Ba évaluation',
    'hr.training': 'Ba formation',
    'hr.documents': 'Mikanda',
    'hr.employee_management': 'Boyangeli ya basali',
    'hr.employee_management_desc': 'Yangela ba profil mpe makambo ya basali',
    'hr.new_employee': 'Mosali ya sika',
    'hr.employee': 'Mosali',
    'hr.code': 'Code',
    'hr.position': 'Esika ya mosala',
    'hr.department': 'Département',
    'hr.status': 'État',
    'hr.actions': 'Misala',
    'hr.modify': 'Kobongola',

    // Relay Points
    'relay.points': 'Bisika ya kozongela',
    'relay.manage_network': 'Yangela réseau ya bisika ya kokamata',
    'relay.new_point': 'Esika ya sika ya kozongela',
    'relay.view_map': 'Tala karte',
    'relay.point': 'Esika ya kozongela',
    'relay.contact': 'Contact',
    'relay.address': 'Adresse',
    'relay.status': 'État',
    'relay.actions': 'Misala',
    'relay.modify': 'Kobongola',

    // Home - Relay Section
    'relay.title': 'Bisika ya kozongela pene na yo',
    'relay.description': 'Luka esika ya kozwa biloko mpo na kozwa ba commande na yo pɛtɛɛ.',
    'relay.step1': 'Koluka',
    'relay.step1.desc': 'Tiká nkombo ya quartier to commune',
    'relay.step2': 'Kopona',
    'relay.step2.desc': 'Poná esika oyo ezalaka pene koleka',
    'relay.step3': 'Kondima',
    'relay.step3.desc': 'Ndima na tango ya kofuta',
    'relay.step4': 'Kokamata',
    'relay.step4.desc': 'Lakisa kɔdi ya kozwa',
    'relay.find_point': 'Luka esika ya kozongela',

    // Orders
    'orders.back_home': 'Zonga na ndako',
    'orders.title': 'Bacommande na ngai',
    'orders.subtitle': 'Landela état ya bacommande na yo nyonso',
    'orders.tabs.all': 'Nyonso',
    'orders.tabs.active': 'Oyo ezali kokende',
    'orders.tabs.completed': 'Oyo esilaki',
    'orders.tabs.cancelled': 'Oyo ebatalami',
    'orders.loading': 'Ezali kotya...',
    'orders.order_number': 'Commande',
    'orders.placed_on': 'Etiami mokolo',
    'orders.view_details': 'Tala makambu',
    'orders.pay_now': 'Futa lelo',
    'orders.buy_again': 'Somba lisusu',
    'orders.paid': 'Efutami',
    'orders.preparing': 'Ezali kobongisama',
    'orders.ready_for_pickup': 'Ezali pene ya kozwa',
    'orders.no_orders': 'Commande ezali te',
    'orders.no_orders_message': 'Otii commande nanu te.',
    'orders.start_shopping': 'Bandá kosomba',
    'orders.filters.title': 'Filtre, koluka mpe kotya na ordre',
    'orders.filters.reset': 'Kozongisa',
    'orders.filters.status': 'État',
    'orders.filters.all_statuses': 'Ba état nyonso',
    'orders.filters.all': 'Ba état nyonso',
    'orders.filters.search': 'Koluka',
    'orders.filters.search_placeholder': 'N° commande, client, moteki...',
    'orders.filters.vendor_search_placeholder': 'N° commande, biloko, esika ya kozwa...',
    'orders.filters.period': 'Ntango',
    'orders.filters.all_periods': 'Bantango nyonso',
    'orders.filters.today': 'Lelo',
    'orders.filters.week': 'Poso oyo',
    'orders.filters.month': 'Sanza oyo',
    'orders.filters.year': 'Mbula oyo',
    'orders.filters.sort_by': 'Tia na ordre',
    'orders.filters.sort_date': 'Mokolo ya bokeli',
    'orders.filters.sort_amount': 'Mbongo',
    'orders.filters.sort_status': 'État',
    'orders.filters.sort_number': 'N° commande',
    'orders.status.PENDING_PAYMENT': 'Ezali kozela kofuta',
    'orders.status.PAID': 'Efutami',
    'orders.status.PREPARING': 'Ezali kobongisama',
    'orders.status.READY_FOR_PICKUP': 'Ezali pene ya kozwa',
    'orders.status.PICKED_UP': 'Ezwami',
    'orders.status.CANCELLED': 'Ebatalami',
    'orders.status.EXPIRED': 'Etikali',

    // Employee management
    'employee.management': 'Boyangeli ya basaleli',
    'employee.total_users': 'Basaleli nyonso',
    'employee.active': 'Oyo bazali kosala',
    'employee.suspended': 'Oyo batelemaki',
    'employee.administrators': 'Bayangeli',
    'employee.user_list': 'Liste ya basaleli',
    'employee.user': 'Mosaleli',
    'employee.role': 'Mosala',
    'employee.status': 'Ndenge ezali',
    'employee.actions': 'Misala',
    'employee.no_name': 'Kombo te',
    'employee.no_phone': 'Telefone te',
    'employee.client': 'Client',
    'employee.admin': 'Moyangeli',
    'employee.super_admin': 'Moyangeli monene',
    'employee.vendor': 'Moteki',
    'employee.relay': 'Esika ya kozongela',
    'employee.search_placeholder': 'Luka mosaleli...',
    'employee.all_roles': 'Misala nyonso',
    'employee.all_status': 'Makambo nyonso',
    'employee.update': 'Kosika',
    'employee.preferences': 'Oyo alingi',
    'employee.statistics': 'Ba chiffre',
    'employee.last_connection': 'Mbala ya suka akotaki',
    'employee.commands': 'Ba commande',

    // Footer
    'footer.description': 'Esika na bino ya kotek biloko na Kinshasa',
    'footer.quick_links': 'Ba lien ya mbangu',
    'footer.categories': 'Mitindo',
    'footer.contact': 'Kosolola',
    'footer.newsletter': 'Nsango',
    'footer.newsletterTitle': 'Nsango',
    'footer.emailPlaceholder': 'Email na yo',
    'footer.about': 'Na ntina na biso',
    'footer.how_it_works': 'Ndenge esalaka',
    'footer.become_vendor': 'Koma moteki',
    'footer.relay_points': 'Bisika ya kozongela',
    'footer.support': 'Lisalisi',
    'footer.email_placeholder': 'Email na yo',
    'footer.copyright': '© Nazandoo. Makoki nyonso ebombi',
    'footer.terms': 'Mibeko ya kosalela',
    'footer.privacy': 'Politiki ya sekele',
    'footer.legal': 'Makambo ya mibeko',
    'footer.secure_payments': 'Kofuta ya libateli',
    'footer.all_rights': 'Makoki nyonso ebombi',

    // Payment Methods
    'payments.title': 'Lolenge ya kofuta',
    'payments.description': 'Sala makambu na bilenge na yo ya kofuta',
    'payments.add': 'Bakisa',
    'payments.addTitle': 'Bakisa lolenge ya kofuta',
    'payments.addCardDescription': 'Koma karte ya banki ya sika',
    'payments.addMobileMoneyDescription': 'Bakisa lolenge ya kofuta Mobile Money',
    'payments.paymentType': 'Lolenge ya kofuta',
    'payments.card': 'Karte ya banki',
    'payments.mobileMoney': 'Mobile Money',
    'payments.provider': 'Operatɛrɛ ya telefone',
    'payments.chooseProvider': 'Pona operatɛrɛ na yo',
    'payments.orangeMoney': 'Orange Money',
    'payments.airtelMoney': 'Airtel Money',
    'payments.mpesa': 'Vodacom M-Pesa',
    'payments.africellMoney': 'Africell Money',
    'payments.phoneNumber': 'Nimero ya telefone',
    'payments.save': 'Bomba',
    'payments.cancel': 'Longola',
    'payments.default': 'Ya liboso',
    'payments.setAsDefault': 'Tia lokola ya liboso',
    'payments.delete': 'Longola',
    'payments.noMethods': 'Lolenge ya kofuta ezali te',
    'payments.addFirstMethod': 'Bakisa lolenge na yo ya liboso ya kofuta',
    'payments.errorLoading': 'Libunga na kotya bilenge ya kofuta',
    'payments.fillAllFields': 'Tondisa bisika nyonso',
    'payments.invalidPhone': 'Format ya nimero ezali malamu te',
    'payments.methodAdded': 'Lolenge ya kofuta ebakisami',
    'payments.errorAdding': 'Libunga na kobakisa',
    'payments.setAsDefaultSuccess': 'Lolenge ya kofuta etiami lokola ya liboso',
    'payments.errorSetDefault': 'Libunga na kobongola',
    'payments.methodDeleted': 'Lolenge ya kofuta elongwami',
    'payments.errorDeleting': 'Libunga na kolongola',

    // Disputes
    'disputes.title': 'Matomba na ngai',
    'disputes.description': 'Landa ndenge matomba na yo ezali',
    'disputes.noDisputes': 'Matomba ezali te',
    'disputes.order': 'Commande',
    'disputes.details': 'Makambu',
    'disputes.detailsTitle': 'Makambu ya matomba',
    'disputes.reason': 'Ntina',
    'disputes.description_label': 'Ndimbola',
    'disputes.status': 'Ezali ndenge nini',
    'disputes.adminResponse': 'Eyano ya administrateur',
    'disputes.createdAt': 'Mokolo ya kokela',
    'disputes.resolvedAt': 'Mokolo ya kosilisa',
    'disputes.defectiveProduct': 'Eloko ebebi',
    'disputes.wrongItem': 'Eloko ya mabe',
    'disputes.missingItem': 'Eloko ezangi',
    'disputes.damaged': 'Eloko ebebi',
    'disputes.notAsDescribed': 'Ekokani te na ndimbola',
    'disputes.other': 'Mosusu',
    'disputes.pending': 'Ezali kozela',
    'disputes.inProgress': 'Ezali kosalama',
    'disputes.resolved': 'Esilisami',
    'disputes.rejected': 'Ebwakani',
    'disputes.errorLoading': 'Libunga na kotya matomba',

    // Messaging
    'messaging.title': 'Masolo',
    'messaging.description': 'Masolo na yo na bateki mpe ba administrateur',
    'messaging.noConversations': 'Masolo ezali te',
    'messaging.unread': 'etangami te',
    'messaging.refreshing': 'Kozongisa sika...',
    'messaging.pullToRefresh': 'Tika mpo na kozongisa sika',
    'messaging.loading': 'Ezali kotia...',
    'messaging.errorLoading': 'Libunga na kotya masolo',
    'messaging.vendor': 'Moteki',

    // Orders
    'orders.recent': 'Ba commande ya sika',
    'orders.recent_desc': 'Landa ba commande mpe ndenge ezali kokende',
    'orders.order': 'Commande',
    'orders.client': 'Client',
    'orders.vendor': 'Moteki',
    'orders.relay_point': 'Esika ya kozongela',
    'orders.amount': 'Mbongo',
    'orders.unknown_product': 'Eloko eyebani te',
    'orders.unknown_client': 'Client eyebani te',
    'orders.unknown_vendor': 'Moteki eyebani te',
    'orders.unknown_relay': 'Esika ya kozongela eyebani te',

    // Categories
    'categories.title': 'Kotala lolenge na biso',
    'categories.description': 'Zwa oyo ozali koluka na lolenge na biso ya ndenge na ndenge',
    'categories.electronics': 'Ba-electronique',
    'categories.fashion': 'Mode mpe kitoko',
    'categories.home': 'Ndako mpe elanga',
    'categories.kids': 'Bana mpe mike',
    'categories.auto': 'Auto mpe moto',
    'categories.games': 'Masano mpe biloko ya masano',
    'categories.books': 'Buku mpe mimeseno',
    'categories.health': 'Nzoto kolongo mpe malamu',
    'categories.automobile': 'Motuka',
    'categories.products_count': 'biloko',
    'categories.products_count_singular': 'eloko',

    // Hero Section
    'hero.title': 'Marketplace Kinshasa',
    'hero.title.location': 'Nazandoo na biso ya mboka',
    'hero.description': 'Mona biloko ya malamu ya mboka mpe ya mikili mosusu na Kinshasa',
    'hero.explore_products': 'Kotala biloko',
    'hero.become_vendor': 'Koma moteki',
    'hero.fast_delivery': 'Kotinda nokinoki',
    'hero.fast_delivery.desc': 'Kotinda na mokolo 1-2',
    'hero.secure_payment': 'Kofuta na libateli',
    'hero.secure_payment.desc': 'Ba-transactions ya libateli',
    'hero.easy_pickup': 'Kozwa pete',
    'hero.easy_pickup.desc': 'Bisika ya kozwa malamu',
    'hero.secure_order': 'Commande ya libateli',
    'hero.satisfied_customers': 'Bakiliya ya esengo',
    'hero.clients_satisfied_label': 'bakiliya ya esengo',

    // Vendors
    'vendor.manage_vendors': 'Simba ba-demandes mpe statut ya bateki',
    'vendor.applications': 'Ba-demandes ya bateki',
    'vendor.review_applications': 'Kotala mpe kosala ba-demandes ya ko-enregistrer bateki ya sika',
    'applications': 'Ba-demandes',
    // Products Section
    'products.title': 'Biloko',
    'products.description': 'Talá biloko oyo bato mingi balingaka',
    'products.no_products': 'Biloko ezali te',
    'products.view_all': 'Monisa biloko nyonso',
    'analytics.title': 'Ba-statistiques',
    'managers': 'Ba-gestionnaires',

    // Vendor Product Management translations - Lingala
    'vendor.product_catalog': 'Mokanda ya biloko',
    'vendor.manage_inventory': 'Salela biloko mpe stock na yo',
    'vendor.new_product': 'Eloko ya sika',
    'product.product': 'Eloko',
    'product.price_label': 'Ntalo',
    'product.stock': 'Stock',
    'product.status': 'Makambo',
    'product.actions': 'Misala',
    'product.lastUpdate': 'Mise à jour ya suka',
    'product.units': 'biloko',
    'product.active': 'Ya kosalema',
    'product.inactive': 'Ya kosala te',
    'product.noCatalog': 'Ezali na eloko moko te na mokanda na yo',
    'product.addFirst': 'Bakisa eloko na yo ya liboso pona kobanda',
    'product.edit': 'Kobongola eloko',
    'product.new': 'Eloko ya sika',
    'product.formDesc': 'Tondisa nsango ya eloko na yo',
    'product.name': 'Nkombo ya eloko',
    'product.price': 'Ntalo',
    'product.currency': 'Mbongo',
    'product.selectCurrency': 'Pona lolenge ya mbongo',
    'product.category': 'Lolenge',
    'product.selectCategory': 'Pona lolenge',
    'product.stockAmount': 'Motango ya stock',
    'product.description': 'Ndimbola',
    'product.images': 'Bililingi',
    'product.save': 'Kobomba',
    'product.cancel': 'Kotika',
    'product.modify': 'Kobongola',
    'product.create': 'Kosala',
    'product.multilingualInfo': 'Makambo ya minoko mingi',
    'product.translating': 'Kozala kobongola...',
    'product.autoTranslate': 'Kobongola automatiquement',
    'product.translationSuccessMessage': 'Mabongoli esalami malamu! Talá pe kobongisa soki esengeli.',
    'product.translationHint': 'Koma nkombo na monoko na yo, sima bongo « Kobongola automatiquement » pona kokela minoko mosusu. Okoki kobongisa yango na maboko na nsima.',
    'product.sourceLanguage': 'Monoko ya liboso',
    'product.namePlaceholder': 'Nkombo ya eloko',
    'product.descriptionPlaceholder': 'Ndimbola ya eloko mobimba',
    'product.translationComplete': 'Libongoli ekokani',
    'product.translationPartial': 'Libongoli ya ndambo',

    // Vendor sections - Stock Management
    'vendor.stock.management': 'Bokambi ya biloko',
    'vendor.stock.overview.totalProducts': 'Biloko nionso',
    'vendor.stock.overview.lowStock': 'Biloko mike',
    'vendor.stock.overview.outOfStock': 'Biloko ebosani',
    'vendor.stock.overview.stockValue': 'Motuya ya biloko',
    'vendor.stock.lowStockLabel': 'Biloko mike (≤5)',
    'vendor.stock.noLowStock': 'Ezali na eloko moko te oyo ezali na biloko mike',
    'vendor.stock.stockByCategory': 'Biloko na lolenge',
    'vendor.stock.units': 'biloko',
    'vendor.stock.alerts.outOfStockProducts': 'Biloko oyo ebosani',
    'vendor.stock.alerts.lowStockProducts': 'Biloko mike (≤ 5 biloko)',
    'vendor.stock.actions.restock': 'Kotisa lisusu',
    'vendor.stock.actions.adjust': 'Kobongisa',
    'vendor.stock.inventory.title': 'Motango ya biloko',
    'vendor.stock.inventory.description': 'Salela motango ya biloko na yo',
    'vendor.stock.table.product': 'Eloko',
    'vendor.stock.table.category': 'Lolenge',
    'vendor.stock.table.currentStock': 'Biloko ya sikawa',
    'vendor.stock.table.unitValue': 'Motuya ya moko',
    'vendor.stock.table.totalValue': 'Motuya mobimba',
    'vendor.stock.table.actions': 'Misala',
    'vendor.stock.uncategorized': 'Ezali na lolenge te',
    'vendor.stock.dialog.title': 'Kobongisa motango',
    'vendor.stock.dialog.adjustmentType': 'Lolenge ya kobongisa',
    'vendor.stock.dialog.stockIn': 'Kokotisa (+)',
    'vendor.stock.dialog.stockOut': 'Kobima (-)',
    'vendor.stock.dialog.adjustment': 'Kobongisa',
    'vendor.stock.dialog.quantity': 'Motango',
    'vendor.stock.dialog.newStock': 'Motango ya sika',
    'vendor.stock.dialog.reason': 'Ntina (soki olingi)',
    'vendor.stock.dialog.reasonPlaceholder': 'Ndakisa: Kotisa lisusu, kozonga ya kiliya, kobongisa...',
    'vendor.stock.dialog.apply': 'Kosalela kobongisa',
    'vendor.stock.success.updated': 'Motango ebongwani',
    'vendor.stock.error.loading': 'Libunga na kotangisa',
    'vendor.stock.error.loadingProducts': 'Libunga na kotangisa biloko',
    'vendor.stock.error.loadingTransactions': 'Libunga na kotangisa misala',
    'vendor.stock.error.invalidQuantity': 'Tia motango oyo ezali malamu.'
  },
  sw: {
    'home': 'Nyumbani',
    'products': 'Bidhaa',
    'categories': 'Makundi',
    'login': 'Ingia',
    'logout': 'Toka',
    'register': 'Jisajili',
    'dashboard': 'Dashibodi',
    'admin': 'Utawala',
    'settings': 'Mipangilio',
    'profile': 'Wasifu',
    'orders': 'Maagizo',
    'cart': 'Kikapu',
    'search': 'Tafuta',
    'loading': 'Inapakia...',
    'error': 'Hitilafu',
    'success': 'Mafanikio',
    'cancel': 'Ghairi',
    'confirm': 'Thibitisha',
    'save': 'Hifadhi',
    'edit': 'Hariri',
    'delete': 'Futa',
    'add': 'Ongeza',
    'create': 'Unda',
    'update': 'Sasisha',
    'view': 'Ona',
    'close': 'Funga',
    'open': 'Fungua',
    'yes': 'Ndio',
    'no': 'Hapana',
    'language': 'Lugha',
    'currency': 'Sarafu',

    // Header
    'header.hello': 'Hujambo',
    'header.menu': 'Menyu',
    'header.home': 'Nyumbani',
    'header.all_products': 'Bidhaa zote',
    'header.your_orders': 'Maagizo yako',
    'header.your_account': 'Akaunti yako',
    'header.vendor_account': 'Akaunti ya muuzaji',
    'header.admin_panel': 'Utawala',
    'header.points': 'Vituo vya kuchukua',
    'header.cart': 'Kikapu',
    'header.search.placeholder': 'Tafuta bidhaa...',
    'header.deals': 'Ofa',
    'header.buyer_account': 'Akaunti ya mnunuzi',
    'header.my_profile': 'Wasifu wangu',
    'header.my_orders': 'Maagizo yangu',
    'header.vendor_dashboard': 'Dashibodi',
    'header.admin_account': 'Akaunti ya msimamizi',
    'header.administration': 'Utawala',
    'header.become_vendor': 'Kuwa muuzaji',
    'header.relay_management': 'Usimamizi wa vituo',
    'header.create_business': 'Unda biashara yangu',
    'header.logout': 'Toka',
    'header.login': 'Ingia',

    // Maandishi ya tovuti
    'site.tagline': 'Soko lako la karibu',
    'site.description': 'Gundua bidhaa bora za ndani na kimataifa moja kwa moja Kinshasa',

    // Auth Page
    'auth.back_to_home': 'Rudi nyumbani',
    'auth.access_account': 'Ingia kwenye akaunti yako',
    'auth.connect_or_create': 'Ingia au unda akaunti mpya',
    'auth.signin': 'Ingia',
    'auth.signup': 'Jisajili',
    'auth.email': 'Barua pepe',
    'auth.password': 'Nywila',
    'auth.full_name': 'Jina kamili',
    'auth.email_placeholder': 'barua@pepe.yako',
    'auth.password_placeholder': '••••••••',
    'auth.full_name_placeholder': 'Jean Mukendi',
    'auth.signin_button': 'Ingia',
    'auth.signup_button': 'Jisajili',
    'auth.signin_loading': 'Kuingia...',
    'auth.signup_loading': 'Kusajili...',
    'auth.forgot_password': 'Umesahau nywila?',
    'auth.forgot_password_title': 'Umesahau Nywila',
    'auth.forgot_password_subtitle': 'Ingiza barua pepe yako na tutakutumia kiungo cha kubadilisha nywila',
    'auth.send_reset_link': 'Tuma kiungo',
    'auth.sending': 'Inatuma...',
    'auth.back_to_login': 'Rudi kwenye kuingia',
    'auth.forgot_password_success_title': 'Barua pepe imetumwa!',
    'auth.forgot_password_success_desc': 'Angalia barua pepe yako ili kubadilisha nywila',
    'auth.forgot_password_error_title': 'Kosa',
    'auth.forgot_password_error_email': 'Tafadhali ingiza barua pepe sahihi',
    'auth.forgot_password_error_general': 'Kosa limetokea. Tafadhali jaribu tena.',
    'auth.forgot_password_email_sent': 'Barua pepe ya kubadilisha imetumwa kwa anwani yako',
    'auth.forgot_password_check_spam': 'Ukiikosa, angalia folda ya spam',
    'auth.send_another_email': 'Tuma barua pepe nyingine',
    'auth.forgot_password_help': 'Unahitaji msaada? Wasiliana nasi kwa',
    'auth.terms_accept': 'Kwa kuingia, unakubali',
    'auth.terms_link': 'masharti ya huduma',
    'auth.privacy_link': 'sera ya faragha',
    'auth.and': 'na',
    'auth.biometric_login': 'Kuingia kwa biometric',
    'auth.use_biometric': 'Tumia {type}',
    'auth.biometric_faceid': 'Face ID',
    'auth.biometric_touchid': 'Touch ID',
    'auth.biometric_fingerprint': 'Alama ya kidole',
    'auth.biometric_authenticate': 'Thibitisha ili uendelee',
    'auth.biometric_setup': 'Weka kuingia kwa biometric',
    'auth.biometric_enabled': 'Kuingia kwa biometric kimewashwa',
    'auth.biometric_error': 'Hitilafu ya uthibitishaji wa biometric',

    // Admin
    'admin.title': 'Utawala',
    'admin.subtitle': 'Dashibodi ya utawala',
    'admin.system_operational': 'Mfumo unafanya kazi',
    'admin.tabs.dashboard': 'Dashibodi',
    'admin.tabs.users': 'Watumiaji',
    'admin.tabs.vendors': 'Wauzaji',
    'admin.tabs.employees': 'Wafanyakazi',
    'admin.tabs.relays': 'Vituo vya kuchukua',
    'admin.tabs.orders': 'Maagizo',
    'admin.tabs.cms': 'CMS',
    'admin.tabs.config': 'Usanidi',
    'admin.tabs.roles': 'Majukumu',
    'admin.tabs.activity': 'Shughuli',
    'admin.tabs.bulk': 'Vitendo vingi',
    'admin.quick_actions': 'Vitendo vya haraka',
    'admin.new_category': 'Kundi jipya',
    'admin.new_page': 'Ukurasa mpya',
    'admin.new_footer_section': 'Sehemu ya chini mpya',
    'admin.cms.loading': 'Inapakia...',
    'admin.cms.editor': 'Mhariri',
    'admin.cms.preview': 'Onyesha kabla',
    'admin.cms.save': 'Hifadhi',
    'admin.cms.hero_tab': 'Hero',
    'admin.cms.sections_tab': 'Sehemu',
    'admin.cms.faq_tab': 'FAQ',
    'admin.cms.hero_title': 'Kichwa cha Hero',
    'admin.cms.hero_description': 'Maelezo ya Hero',
    'admin.cms.hero_title_placeholder': 'Kichwa kikuu cha ukurasa',
    'admin.cms.hero_description_placeholder': 'Maelezo chini ya kichwa',
    'admin.cms.section_title': 'Kichwa cha Sehemu',
    'admin.cms.section_content': 'Maudhui',
    'admin.cms.section_title_placeholder': 'Kichwa cha sehemu',
    'admin.cms.section_content_placeholder': 'Maudhui ya sehemu',
    'admin.cms.faq_title': 'Maswali Yanayoulizwa Mara kwa Mara',
    'admin.cms.add_faq': 'Ongeza Swali',
    'admin.cms.question': 'Swali',
    'admin.cms.answer': 'Jibu',
    'admin.cms.question_placeholder': 'Ingiza swali',
    'admin.cms.answer_placeholder': 'Ingiza jibu',
    'admin.cms.saved': 'Maudhui Yamehifadhiwa',
    'admin.cms.saved_description': 'Mabadiliko yamehifadhiwa kwa mafanikio',
    'admin.cms.error': 'Hitilafu',
    'admin.cms.no_faq': 'Hakuna maswali yaliyoongezwa. Bonyeza "Ongeza Swali" kuanza.',
    'admin.translation_checker': 'Mkaguzi wa tafsiri',
    'admin.translation_checker_description': 'Kugundua kiotomatiki maneno ya moja kwa moja bila tafsiri katika msimbo wako',
    'admin.scanning': 'Inachunguza...',
    'admin.start_scan': 'Anza Uchunguzi',
    'admin.scan_complete': 'Uchunguzi umekamilika',
    'admin.issues_found': 'matatizo yalipatikana',
    'admin.scan_error': 'Hitilafu wakati wa uchunguzi',
    'admin.files_scanned': 'Faili zilizochunguzwa',
    'admin.files_with_issues': 'Faili zenye matatizo',
    'admin.files_ok': 'Faili sawa',
    'admin.affected_files': 'Faili zilizoathirika',
    'admin.issues_in_file': 'Matatizo katika faili hii',
    'admin.all_issues': 'Matatizo yote',

    // About Page
    'about.hero_title': 'Kuhusu Sisi',
    'about.hero_description': 'Soko lako la kuaminika kwa ununuzi salama wa karibu',
    'about.mission_title': 'Dhamira Yetu',
    'about.values_title': 'Maadili Yetu',
    'about.security': 'Usalama',
    'about.security_description': 'Miamala salama na ulinzi wa data binafsi',
    'about.community': 'Jamii',
    'about.community_description': 'Kujenga jamii ya kusaidia na kushirikisha',
    'about.reliability': 'Utegemezi',
    'about.reliability_description': 'Huduma ya ubora na mtandao wa vituo vilivyoidhinishwa',
    'about.innovation': 'Uvumbuzi',
    'about.innovation_description': 'Teknolojia za kisasa kwa uzoefu bora wa mtumiaji',
    'about.active_vendors': 'Wauzaji Hai',
    'about.satisfied_customers': 'Wateja Wenye Kuridhika',
    'about.relay_points': 'Vituo vya Kuchukua',

    // How It Works Page
    'howItWorks.hero_title': 'Jinsi Inavyofanya Kazi',
    'howItWorks.hero_description': 'Gundua jinsi ya kutumia jukwaa letu kwa urahisi',
    'howItWorks.buyers_title': 'Kwa Wanunuzi',
    'howItWorks.sellers_title': 'Kwa Wauzaji',
    'howItWorks.advantages_title': 'Faida Zetu',
    'howItWorks.step1_title': 'Tafuta bidhaa',
    'howItWorks.step1_description': 'Vinjari katalogi yetu na upate bidhaa zinazokuvutia',
    'howItWorks.step2_title': 'Chagua kituo cha kuchukua',
    'howItWorks.step2_description': 'Chagua kituo cha kuchukua kilicho karibu nawe',
    'howItWorks.step3_title': 'Lipa mtandaoni',
    'howItWorks.step3_description': 'Fanya malipo yako kwa usalama kupitia jukwaa letu',
    'howItWorks.step4_title': 'Chukua agizo lako',
    'howItWorks.step4_description': 'Onyesha kituo cha kuchukua na msimbo wako wa QR ili kupokea bidhaa zako',
    'howItWorks.seller_step1_title': 'Unda duka lako',
    'howItWorks.seller_step1_description': 'Jisajili na uunde nafasi yako ya muuzaji kwa mibofyo michache',
    'howItWorks.seller_step2_title': 'Ongeza bidhaa zako',
    'howItWorks.seller_step2_description': 'Chapisha vitu vyako na picha na maelezo kamili',
    'howItWorks.seller_step3_title': 'Peleka kwenye kituo',
    'howItWorks.seller_step3_description': 'Weka maagizo kwenye kituo cha kuchukua kilicho karibu',
    'howItWorks.advantage1_title': 'Usalama wa juu',
    'howItWorks.advantage1_description': 'Miamala yako inalindwa na data yako ni salama',
    'howItWorks.advantage2_title': 'Kubadilika kwa malipo',
    'howItWorks.advantage2_description': 'Chaguzi nyingi za malipo zinapatikana',
    'howItWorks.advantage3_title': 'Msaada wa karibu',
    'howItWorks.advantage3_description': 'Unga mkono uchumi wa karibu kwa kununua karibu nawe',

    // Support Page
    'support.hero_title': 'Usaidizi wa Mteja',
    'support.hero_description': 'Tuko hapa kukusaidia',
    'support.contact_title': 'Wasiliana Nasi',
    'support.contact_description': 'Njia nyingi za kutufikia',
    'support.email': 'Barua pepe',
    'support.phone': 'Simu',
    'support.live_chat': 'Mazungumzo ya moja kwa moja',
    'support.form_title': 'Tutumie ujumbe',
    'support.form_description': 'Jaza fomu iliyo hapa chini',
    'support.faq_title': 'Maswali Yanayoulizwa Mara kwa Mara',
    'support.faq_description': 'Pata majibu haraka',
    'support.hours_title': 'Saa za Kufungua',
    'support.hours_description': 'Tunapatikana',
    'support.weekdays': 'Jumatatu - Ijumaa',
    'support.weekend': 'Jumamosi - Jumapili',
    'support.form_name': 'Jina kamili',
    'support.form_email': 'Barua pepe',
    'support.form_subject': 'Mada',
    'support.form_message': 'Ujumbe',
    'support.form_submit': 'Tuma',
    'support.form_sending': 'Inatuma...',
    'support.form_success': 'Ujumbe umetumwa',
    'support.form_success_description': 'Tutakujibu haraka iwezekanavyo',
    'support.form_error': 'Hitilafu ya kutuma',
    
    // Relay Points Page
    'relayPoints.hero_title': 'Vituo vya Kuchukua',
    'relayPoints.hero_description': 'Pata kituo cha kuchukua kilicho karibu',
    'relayPoints.search_placeholder': 'Tafuta kwa jina, anwani au manispaa...',
    'relayPoints.how_it_works_title': 'Jinsi inavyofanya kazi?',
    'relayPoints.how_it_works_description': 'Chukua maagizo yako katika hatua 4 rahisi',
    'relayPoints.no_results': 'Hakuna kituo cha kuchukua kilichopatikana',
    'relayPoints.loading': 'Inapakia vituo vya kuchukua...',
    'relayPoints.localize': 'Pata mahali',
    'relayPoints.itinerary': 'Maelekezo',
    'relayPoints.contact': 'Wasiliana',
    'relayPoints.choose': 'Chagua',
    'relayPoints.selected': 'Kituo cha kuchukua kimechaguliwa',
    'admin.analytics.users': 'Watumiaji',
    'admin.analytics.vendors': 'Wauzaji',
    'admin.analytics.orders': 'Maagizo',
    'admin.analytics.revenue': 'Mapato',
    'admin.analytics.employees': 'Wafanyakazi',

    // CMS Tabs
    'cms.tabs.dashboard': 'Dashibodi',
    'cms.tabs.settings': 'Mipangilio',
    'cms.tabs.categories': 'Makundi',
    'cms.tabs.menus': 'Menyu',
    'cms.tabs.footer': 'Kijachini',
    'cms.tabs.pages': 'Kurasa',

    // User Dashboard
    'dashboard.title': 'Dashibodi',
    'dashboard.welcome': 'Karibu',
    'dashboard.logout': 'Toka',
    'dashboard.total_orders': 'Jumla ya maagizo',
    'dashboard.pending_orders': 'Maagizo yanayosubiri',
    'dashboard.completed_orders': 'Maagizo yaliyokamilika',
    'dashboard.my_orders_tab': 'Maagizo yangu',
    'dashboard.payments_tab': 'Malipo',
    'dashboard.disputes_tab': 'Malalamiko',
    'dashboard.messages_tab': 'Ujumbe',
    'dashboard.account_settings_tab': 'Mipangilio ya akaunti',
    'dashboard.my_orders_title': 'Maagizo yangu',
    'dashboard.my_orders_desc': 'Fuata maagizo yako na hali yake',
    'dashboard.no_orders': 'Huna maagizo bado',
    'dashboard.discover_products': 'Gundua bidhaa',
    'dashboard.myOrders': 'Maagizo yangu',
    'dashboard.payments': 'Malipo',
    'dashboard.disputes': 'Malalamiko',
    'dashboard.messages': 'Ujumbe',
    'dashboard.accountSettings': 'Mipangilio ya akaunti',
    'dashboard.defaultRelay': 'Kituo cha kuchukua cha kawaida',
    'dashboard.selectRelayPreference': 'Chagua kituo chako cha kuchukua unachopendelea kwa maagizo yako ya baadaye',
    'dashboard.relayPoint': 'Kituo cha kuchukua',
    'dashboard.register': 'Hifadhi',
    'dashboard.selectRelay': 'Chagua kituo cha kuchukua',
    'dashboard.saving': 'Inahifadhi...',
    'dashboard.selectRelayError': 'Tafadhali chagua kituo cha kuchukua',
    'dashboard.relayUpdated': 'Kituo cha kuchukua cha kawaida kimehifadhiwa',
    'dashboard.updateError': 'Hitilafu wakati wa kuhifadhi',
    'dashboard.pickupCode': 'Msimbo wa kuchukua',
    'dashboard.presentCode': 'Onyesha msimbo huu kwenye kituo cha kuchukua ili kupokea agizo lako',
    'dashboard.expiresOn': 'Inaisha',
    'dashboard.viewQRCode': 'Angalia msimbo wa QR',
    'dashboard.viewDetails': 'Angalia maelezo',
    'dashboard.cancelOrder': 'Ghairi agizo',
    'dashboard.cancelOrderTitle': 'Ghairi agizo?',
    'dashboard.cancelOrderConfirm': 'Je, unataka kughairi agizo',
    'dashboard.cancelOrderWarning': 'Hatua hii ni ya mwisho na haiwezi kutenduliwa.',
    'dashboard.back': 'Rudi',
    'dashboard.confirmCancel': 'Thibitisha kughairi',

    // Account Settings (Dashboard)
    'accountSettings.personalInfo': 'Taarifa za kibinafsi',
    'accountSettings.manageProfile': 'Simamia taarifa za wasifu wako',
    'accountSettings.fullName': 'Jina kamili',
    'accountSettings.phone': 'Simu',
    'accountSettings.businessName': 'Jina la biashara',
    'accountSettings.notProvided': 'Haijatolewea',
    'accountSettings.editInfo': 'Badilisha taarifa',
    'accountSettings.businessNamePlaceholder': 'Mfano: Duka langu',
    'accountSettings.saving': 'Inahifadhi...',
    'accountSettings.save': 'Hifadhi',
    'accountSettings.cancel': 'Ghairi',
    'accountSettings.emailAddress': 'Anwani ya barua pepe',
    'accountSettings.modifyEmail': 'Badilisha anwani yako ya barua pepe',
    'accountSettings.currentEmail': 'Barua pepe ya sasa',
    'accountSettings.changeEmail': 'Badilisha barua pepe',
    'accountSettings.newEmail': 'Barua pepe mpya',
    'accountSettings.important': 'Muhimu',
    'accountSettings.emailChangeNote': 'Utapokea barua pepe ya uthibitisho ili kuthibitisha anwani yako mpya.',
    'accountSettings.updating': 'Inabadilisha...',
    'accountSettings.updateEmail': 'Badilisha barua pepe',
    'accountSettings.password': 'Nenosiri',
    'accountSettings.changePassword': 'Badilisha nenosiri lako',
    'accountSettings.newPassword': 'Nenosiri jipya',
    'accountSettings.confirmPassword': 'Thibitisha nenosiri',
    'accountSettings.updatePassword': 'Badilisha nenosiri',
    'accountSettings.profileSettings': 'Mapendeleo ya wasifu',
    'accountSettings.customizeExperience': 'Unda uzoefu wako',
    'accountSettings.language': 'Lugha',
    'accountSettings.currency': 'Sarafu',

      // Store Settings
      'settings.withdrawalDefaults': 'Mipangilio ya msingi ya kutoa pesa',
      'settings.withdrawalDefaultsDesc': 'Sanidi taarifa zako kwa ajili ya kutoa pesa (mobile money au uhamisho wa benki)',
      'settings.mobileNumber': 'Nambari ya simu ya mkononi',
      'settings.mobileNumberPlaceholder': 'Mfano: +243812345678',
      'settings.mobileProvider': 'Mtoa huduma wa malipo ya simu',
      'settings.selectMobileProvider': 'Chagua mtoa huduma',
      'settings.bankAccount': 'Nambari ya akaunti ya benki',
      'settings.bankAccountPlaceholder': 'Mfano: 1234567890',
      'settings.bankName': 'Jina la benki',
      'settings.bankNamePlaceholder': 'Mfano: BCDC, Equity Bank, n.k.',

    // Order Status
    'order.status.pending_payment': 'Inasubiri malipo',
    'order.status.paid': 'Imelipwa',
    'order.status.awaiting_drop_off': 'Inasubiri kuachwa',
    'order.status.available_for_pickup': 'Tayari kuchukua',
    'order.status.picked_up': 'Imechukuliwa',
    'order.status.expired': 'Imeisha muda',
    'order.status.dispute': 'Mgogoro',
    'order.status.cancelled': 'Imeghairiwa',

    'admin.employees.title': 'Usimamizi wa Watumiaji',
    'admin.employees.subtitle': 'Simamia akaunti za watumiaji na ruhusa zao',
    'admin.users.export': 'Hamisha',
    'admin.users.new_user': 'Mtumiaji mpya',
    'admin.config.categories.general': 'Jumla',
    'admin.config.categories.general_desc': 'Mipangilio ya jumla ya tovuti',
    'admin.config.site_info': 'Taarifa za tovuti',
    'admin.config.site_name': 'Jina la tovuti',
    'admin.config.slogan': 'Kauli mbiu',
    'admin.config.slogan_desc': 'Maelezo mafupi ya jukwaa lako',
    'admin.config.description': 'Maelezo',
    'admin.config.description_desc': 'Maelezo ya kina kwa SEO',
    'admin.config.reset': 'Weka upya',
    'admin.config.save_all': 'Hifadhi yote',
    'admin.config.advanced': 'Usanidi wa hali ya juu',
    'admin.config.advanced_desc': 'Rekebishia vipengele vyote vya jukwaa lako',
    'admin.config.categories': 'Makundi',
    'admin.config.appearance': 'Muonekano',
    'admin.config.appearance_desc': 'Urekebishaji wa kuonekana',
    'admin.config.payments': 'Malipo',
    'admin.config.payments_desc': 'Usanidi wa malipo',
    'admin.config.delivery': 'Uwasilishaji',
    'admin.config.delivery_desc': 'Mipangilio ya uwasilishaji',
    'admin.config.notifications': 'Arifa',
    'admin.config.notifications_desc': 'Usimamizi wa arifa',

    // HR Management
    'hr.total_employees': 'Wafanyakazi wote',
    'hr.active': 'Hai',
    'hr.positions': 'Nafasi',
    'hr.departments': 'Idara',
    'hr.employees': 'Wafanyakazi',
    'hr.leave': 'Likizo',
    'hr.evaluations': 'Utathminisho',
    'hr.training': 'Mafunzo',
    'hr.documents': 'Hati',
    'hr.employee_management': 'Usimamizi wa wafanyakazi',
    'hr.employee_management_desc': 'Simamia wasifu na taarifa za wafanyakazi',
    'hr.new_employee': 'Mfanyakazi mpya',
    'hr.employee': 'Mfanyakazi',
    'hr.code': 'Msimbo',
    'hr.position': 'Nafasi',
    'hr.department': 'Idara',
    'hr.status': 'Hali',
    'hr.actions': 'Vitendo',
    'hr.modify': 'Rekebishia',

    // Relay Points
    'relay.points': 'Vituo vya kuchukua',
    'relay.manage_network': 'Simamia mtandao wa vituo vya kuchukua',
    'relay.new_point': 'Kituo kipya cha kuchukua',
    'relay.view_map': 'Ona ramani',
    'relay.point': 'Kituo cha kuchukua',
    'relay.contact': 'Anwani',
    'relay.address': 'Anuani',
    'relay.status': 'Hali',
    'relay.actions': 'Vitendo',
    'relay.modify': 'Rekebishia',

    // Home - Relay Section
    'relay.title': 'Vituo vya kuchukua karibu na wewe',
    'relay.description': 'Tafuta kituo cha kuchukua ili kuchukua maagizo yako kwa urahisi.',
    'relay.step1': 'Tafuta',
    'relay.step1.desc': 'Ingiza mtaa au jimbo lako',
    'relay.step2': 'Chagua',
    'relay.step2.desc': 'Chagua kituo kilicho karibu',
    'relay.step3': 'Thibitisha',
    'relay.step3.desc': 'Thibitisha wakati wa malipo',
    'relay.step4': 'Chukua',
    'relay.step4.desc': 'Onyesha nambari yako ya uchukuzi',
    'relay.find_point': 'Tafuta kituo cha kuchukua',

    // Orders
    'orders.back_home': 'Rudi nyumbani',
    'orders.title': 'Maagizo Yangu',
    'orders.subtitle': 'Fuatilia hali ya maagizo yako yote',
    'orders.tabs.all': 'Yote',
    'orders.tabs.active': 'Yanayoendelea',
    'orders.tabs.completed': 'Zilizokamilika',
    'orders.tabs.cancelled': 'Zilizoghairiwa',
    'orders.loading': 'Inapakia...',
    'orders.order_number': 'Agizo',
    'orders.placed_on': 'Imeagizwa tarehe',
    'orders.view_details': 'Angalia maelezo',
    'orders.pay_now': 'Lipa sasa',
    'orders.buy_again': 'Nunua tena',
    'orders.paid': 'Imelipwa',
    'orders.preparing': 'Inaandaliwa',
    'orders.ready_for_pickup': 'Tayari kuchukua',
    'orders.no_orders': 'Hakuna maagizo',
    'orders.no_orders_message': 'Bado hujaagiza chochote.',
    'orders.start_shopping': 'Anza ununuzi',
    'orders.filters.title': 'Chujio, utafutaji na kupanga',
    'orders.filters.reset': 'Rudisha',
    'orders.filters.status': 'Hali',
    'orders.filters.all_statuses': 'Hali zote',
    'orders.filters.all': 'Hali zote',
    'orders.filters.search': 'Tafuta',
    'orders.filters.search_placeholder': 'N° agizo, mteja, muuzaji...',
    'orders.filters.vendor_search_placeholder': 'N° agizo, bidhaa, kituo cha kuchukua...',
    'orders.filters.period': 'Kipindi',
    'orders.filters.all_periods': 'Vipindi vyote',
    'orders.filters.today': 'Leo',
    'orders.filters.week': 'Wiki hii',
    'orders.filters.month': 'Mwezi huu',
    'orders.filters.year': 'Mwaka huu',
    'orders.filters.sort_by': 'Panga kwa',
    'orders.filters.sort_date': 'Tarehe ya kuagiza',
    'orders.filters.sort_amount': 'Kiasi',
    'orders.filters.sort_status': 'Hali',
    'orders.filters.sort_number': 'N° agizo',
    'orders.status.PENDING_PAYMENT': 'Inasubiri malipo',
    'orders.status.PAID': 'Imelipwa',
    'orders.status.PREPARING': 'Inatengenezwa',
    'orders.status.READY_FOR_PICKUP': 'Tayari kuchukuliwa',
    'orders.status.PICKED_UP': 'Imechukuliwa',
    'orders.status.CANCELLED': 'Imeghairiwa',
    'orders.status.EXPIRED': 'Imeisha muda',

    // Employee management
    'employee.management': 'Usimamizi wa watumiaji',
    'employee.total_users': 'Watumiaji wote',
    'employee.active': 'Hai',
    'employee.suspended': 'Wamesimamishwa',
    'employee.administrators': 'Wasimamizi',
    'employee.user_list': 'Orodha ya watumiaji',
    'employee.user': 'Mtumiaji',
    'employee.role': 'Jukumu',
    'employee.status': 'Hali',
    'employee.actions': 'Vitendo',
    'employee.no_name': 'Hakuna jina',
    'employee.no_phone': 'Hakuna simu',
    'employee.client': 'Mteja',
    'employee.admin': 'Msimamizi',
    'employee.super_admin': 'Msimamizi Mkuu',
    'employee.vendor': 'Muuzaji',
    'employee.relay': 'Kituo cha kuchukua',
    'employee.search_placeholder': 'Tafuta mtumiaji...',
    'employee.all_roles': 'Majukumu yote',
    'employee.all_status': 'Hali zote',
    'employee.update': 'Sasisha',
    'employee.preferences': 'Mapendeleo',
    'employee.statistics': 'Takwimu',
    'employee.last_connection': 'Muunganiko wa mwisho',
    'employee.commands': 'Maagizo',

    // Footer
    'footer.description': 'Sokoni lako la kuaminiwa huko Kinshasa',
    'footer.quick_links': 'Viungo vya haraka',
    'footer.categories': 'Makundi',
    'footer.contact': 'Mawasiliano',
    'footer.newsletter': 'Jarida',
    'footer.about': 'Kuhusu',
    'footer.how_it_works': 'Jinsi inavyofanya kazi',
    'footer.become_vendor': 'Kuwa muuzaji',
    'footer.relay_points': 'Vituo vya kuchukua',
    'footer.support': 'Msaada',
    'footer.email_placeholder': 'Barua pepe yako',
    'footer.copyright': '© Nazandoo. Haki zote zimehifadhiwa',
    'footer.terms': 'Masharti ya Huduma',
    'footer.privacy': 'Sera ya Faragha',
    'footer.legal': 'Tangazo la Kisheria',
    'footer.secure_payments': 'Malipo salama',
    'footer.all_rights': 'Haki zote zimehifadhiwa',
    'footer.newsletterTitle': 'Jarida',
    'footer.emailPlaceholder': 'barua@yako.com',
    'footer.subscribeButton': 'Jiandikishe',
    'footer.subscribing': 'Inasajili...',
    'footer.invalidEmail': 'Barua pepe si sahihi',
    'footer.emailTooLong': 'Barua pepe ni ndefu sana',
    'footer.subscribeError': 'Hitilafu ya usajili',
    'footer.subscribeSuccess': 'Umesajiliwa! Angalia sanduku lako la barua pepe.',

    // Payment Methods
    'payments.title': 'Njia za malipo',
    'payments.description': 'Simamia njia zako za malipo zilizosajiliwa',
    'payments.add': 'Ongeza',
    'payments.addTitle': 'Ongeza njia ya malipo',
    'payments.addCardDescription': 'Sajili kadi mpya ya benki',
    'payments.addMobileMoneyDescription': 'Ongeza njia mpya ya malipo ya Mobile Money',
    'payments.paymentType': 'Aina ya malipo',
    'payments.card': 'Kadi ya benki',
    'payments.mobileMoney': 'Mobile Money',
    'payments.provider': 'Mtoa huduma wa simu',
    'payments.chooseProvider': 'Chagua mtoa huduma wako',
    'payments.orangeMoney': 'Orange Money',
    'payments.airtelMoney': 'Airtel Money',
    'payments.mpesa': 'Vodacom M-Pesa',
    'payments.africellMoney': 'Africell Money',
    'payments.phoneNumber': 'Nambari ya simu',
    'payments.save': 'Hifadhi',
    'payments.cancel': 'Ghairi',
    'payments.default': 'Ya msingi',
    'payments.setAsDefault': 'Weka kama ya msingi',
    'payments.delete': 'Futa',
    'payments.noMethods': 'Hakuna njia ya malipo iliyosajiliwa',
    'payments.addFirstMethod': 'Ongeza njia yako ya kwanza ya malipo',
    'payments.errorLoading': 'Hitilafu ya kupakia njia za malipo',
    'payments.fillAllFields': 'Tafadhali jaza sehemu zote',
    'payments.invalidPhone': 'Muundo wa nambari si sahihi',
    'payments.methodAdded': 'Njia ya malipo imeongezwa',
    'payments.errorAdding': 'Hitilafu ya kuongeza',
    'payments.setAsDefaultSuccess': 'Njia ya malipo imewekwa kama ya msingi',
    'payments.errorSetDefault': 'Hitilafu ya kusasisha',
    'payments.methodDeleted': 'Njia ya malipo imefutwa',
    'payments.errorDeleting': 'Hitilafu ya kufuta',

    // Disputes
    'disputes.title': 'Malalamiko yangu',
    'disputes.description': 'Fuata hali ya malalamiko yako ya bidhaa',
    'disputes.noDisputes': 'Hakuna malalamiko yanayoendelea',
    'disputes.order': 'Agizo',
    'disputes.details': 'Maelezo',
    'disputes.detailsTitle': 'Maelezo ya lalamiko',
    'disputes.reason': 'Sababu',
    'disputes.description_label': 'Maelezo',
    'disputes.status': 'Hali',
    'disputes.adminResponse': 'Jibu la msimamizi',
    'disputes.createdAt': 'Tarehe ya kutengenezwa',
    'disputes.resolvedAt': 'Tarehe ya kutatuliwa',
    'disputes.defectiveProduct': 'Bidhaa ina hitilafu',
    'disputes.wrongItem': 'Bidhaa isiyo sahihi',
    'disputes.missingItem': 'Bidhaa haikupatikana',
    'disputes.damaged': 'Bidhaa imeharibika',
    'disputes.notAsDescribed': 'Hailingani na maelezo',
    'disputes.other': 'Nyingine',
    'disputes.pending': 'Inasubiri',
    'disputes.inProgress': 'Inafanywa kazi',
    'disputes.resolved': 'Imetatuliwa',
    'disputes.rejected': 'Imekataliwa',
    'disputes.errorLoading': 'Hitilafu ya kupakia malalamiko',

    // Messaging
    'messaging.title': 'Ujumbe',
    'messaging.description': 'Mazungumzo yako na wauzaji na wasimamizi',
    'messaging.noConversations': 'Hakuna mazungumzo',
    'messaging.unread': 'haijasomwa',
    'messaging.refreshing': 'Inasasisha...',
    'messaging.pullToRefresh': 'Achia ili kusasisha',
    'messaging.loading': 'Inapakia...',
    'messaging.errorLoading': 'Hitilafu ya kupakia ujumbe',
    'messaging.vendor': 'Muuzaji',

    // Orders
    'orders.recent': 'Maagizo ya karibuni',
    'orders.recent_desc': 'Fuata maagizo na maendeleo yake',
    'orders.order': 'Agizo',
    'orders.client': 'Mteja',
    'orders.vendor': 'Muuzaji',
    'orders.relay_point': 'Kituo cha kuchukua',
    'orders.amount': 'Kiasi',
    'orders.unknown_product': 'Bidhaa haijulikani',
    'orders.unknown_client': 'Mteja hajulikani',
    'orders.unknown_vendor': 'Muuzaji hajulikani',
    'orders.unknown_relay': 'Kituo cha kuchukua hakijulikani',

    // Categories
    'categories.title': 'Chunguza aina zetu',
    'categories.description': 'Pata kile unachotafuta katika aina zetu mbalimbali',
    'categories.electronics': 'Elektroniki',
    'categories.fashion': 'Mitindo na Urembo',
    'categories.home': 'Nyumba na Bustani',
    'categories.kids': 'Watoto na Wachanga',
    'categories.auto': 'Magari na Pikipiki',
    'categories.games': 'Michezo na Vichezeo',
    'categories.books': 'Vitabu na Utamaduni',
    'categories.health': 'Afya na Ustawi',
    'categories.automobile': 'Magari',
    'categories.products_count': 'bidhaa',
    'categories.products_count_singular': 'bidhaa',

    // Hero Section
    'hero.title': 'Soko la Kinshasa',
    'hero.title.location': 'Soko lako la ndani',
    'hero.description': 'Gundua bidhaa bora za ndani na kimataifa moja kwa moja Kinshasa',
    'hero.explore_products': 'Chunguza bidhaa',
    'hero.become_vendor': 'Kuwa mchuuzi',
    'hero.fast_delivery': 'Utoaji wa haraka',
    'hero.fast_delivery.desc': 'Utoaji wa siku 1-2',
    'hero.secure_payment': 'Malipo salama',
    'hero.secure_payment.desc': 'Miamala iliyolindwa',
    'hero.easy_pickup': 'Kuchukua rahisi',
    'hero.easy_pickup.desc': 'Vituo vya kuchukua vyenye faida',
    'hero.secure_order': 'Agizo salama',
    'hero.satisfied_customers': 'Wateja wenye furaha',
    'hero.clients_satisfied_label': 'wateja wenye furaha',

    // Vendors
    'vendor.manage_vendors': 'Simamia maombi na hali ya wachuuzi',
    'vendor.applications': 'Maombi ya wachuuzi',
    'vendor.review_applications': 'Kagua na kushughulikia maombi ya usajili wa wachuuzi wapya',
    'applications': 'Maombi',
    // Products Section
    'products.title': 'Bidhaa',
    'products.description': 'Gundua bidhaa zetu maarufu zaidi',
    'products.no_products': 'Hakuna bidhaa kwa sasa',
    'products.view_all': 'Tazama bidhaa zote',
    'analytics.title': 'Takwimu',
    'managers': 'Wasimamizi',

    // Vendor Product Management translations - Swahili
    'vendor.product_catalog': 'Katalogi ya bidhaa',
    'vendor.manage_inventory': 'Simamia hesabu na hisa zako',
    'vendor.new_product': 'Bidhaa mpya',
    'product.product': 'Bidhaa',
    'product.price_label': 'Bei',
    'product.stock': 'Hisa',
    'product.status': 'Hali',
    'product.actions': 'Vitendo',
    'product.lastUpdate': 'Sasisho la mwisho',
    'product.units': 'vipimo',
    'product.active': 'Inatumika',
    'product.inactive': 'Haitumiki',
    'product.noCatalog': 'Hakuna bidhaa katika katalogi yako',
    'product.addFirst': 'Ongeza bidhaa yako ya kwanza ili kuanza',
    'product.edit': 'Hariri bidhaa',
    'product.new': 'Bidhaa mpya',
    'product.formDesc': 'Jaza maelezo ya bidhaa yako',
    'product.name': 'Jina la bidhaa',
    'product.price': 'Bei',  
    'product.currency': 'Sarafu',
    'product.selectCurrency': 'Chagua sarafu',
    'product.category': 'Aina',
    'product.selectCategory': 'Chagua aina',
    'product.stockAmount': 'Kiasi cha hisa',
    'product.description': 'Maelezo',
    'product.images': 'Picha',
    'product.save': 'Hifadhi',
    'product.cancel': 'Ghairi',
    'product.modify': 'Badilisha',
    'product.create': 'Unda',
    'product.multilingualInfo': 'Taarifa za lugha nyingi',
    'product.translating': 'Inatafsiri...',
    'product.autoTranslate': 'Tafsiri kiotomatiki',
    'product.translationSuccessMessage': 'Tafsiri zimefanikiwa! Kagua na urekebishe ikiwa ni lazima.',
    'product.translationHint': 'Andika jina kwa lugha yako, kisha bonyeza "Tafsiri kiotomatiki" kupata lugha zingine. Unaweza kuhariri kwa mkono baadaye.',
    'product.sourceLanguage': 'Lugha ya asili',
    'product.namePlaceholder': 'Jina la bidhaa',
    'product.descriptionPlaceholder': 'Maelezo kamili ya bidhaa',
    'product.translationComplete': 'Tafsiri kamili',
    'product.translationPartial': 'Tafsiri ya sehemu',

    // Vendor sections - Stock Management
    'vendor.stock.management': 'Usimamizi wa hisa',
    'vendor.stock.overview.totalProducts': 'Bidhaa zote',
    'vendor.stock.overview.lowStock': 'Hisa kidogo',
    'vendor.stock.overview.outOfStock': 'Hisa zimeisha',
    'vendor.stock.overview.stockValue': 'Thamani ya hisa',
    'vendor.stock.lowStockLabel': 'Hisa kidogo (≤5)',
    'vendor.stock.noLowStock': 'Hakuna bidhaa zenye hisa kidogo',
    'vendor.stock.stockByCategory': 'Hisa kwa aina',
    'vendor.stock.units': 'vipimo',
    'vendor.stock.alerts.outOfStockProducts': 'Bidhaa zimeisha',
    'vendor.stock.alerts.lowStockProducts': 'Hisa kidogo (≤ vipimo 5)',
    'vendor.stock.actions.restock': 'Jaza tena',
    'vendor.stock.actions.adjust': 'Rekebisha',
    'vendor.stock.inventory.title': 'Hesabu ya bidhaa',
    'vendor.stock.inventory.description': 'Simamia viwango vya hisa za bidhaa zako',
    'vendor.stock.table.product': 'Bidhaa',
    'vendor.stock.table.category': 'Aina',
    'vendor.stock.table.currentStock': 'Hisa ya sasa',
    'vendor.stock.table.unitValue': 'Thamani ya kipimo',
    'vendor.stock.table.totalValue': 'Thamani jumla',
    'vendor.stock.table.actions': 'Vitendo',
    'vendor.stock.uncategorized': 'Haina aina',
    'vendor.stock.dialog.title': 'Rekebisha hisa',
    'vendor.stock.dialog.adjustmentType': 'Aina ya marekebisho',
    'vendor.stock.dialog.stockIn': 'Hisa ndani (+)',
    'vendor.stock.dialog.stockOut': 'Hisa nje (-)',
    'vendor.stock.dialog.adjustment': 'Marekebisho',
    'vendor.stock.dialog.quantity': 'Kiasi',
    'vendor.stock.dialog.newStock': 'Hisa mpya',
    'vendor.stock.dialog.reason': 'Sababu (si lazima)',
    'vendor.stock.dialog.reasonPlaceholder': 'Mfano: Kujaza tena, kurudi kwa mteja, marekebisho...',
    'vendor.stock.dialog.apply': 'Tekeleza marekebisho',
    'vendor.stock.success.updated': 'Hisa imesasishwa',
    'vendor.stock.error.loading': 'Hitilafu katika kupakia data',
    'vendor.stock.error.loadingProducts': 'Hitilafu katika kupakia bidhaa',
    'vendor.stock.error.loadingTransactions': 'Hitilafu katika kupakia miamala',
    'vendor.stock.error.invalidQuantity': 'Tafadhali ingiza kiasi sahihi.'
  }
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    return savedLanguage || 'fr';
  });
  const [isLoading, setIsLoading] = useState(false);

  // Charger les traductions depuis la base de données
  const loadTranslations = useCallback(async (force = false) => {
    if ((Object.keys(translationCache).length === 0 || force) && !isLoading) {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('translations')
          .select('*')
          .order('key');

        if (error) throw error;

        if (data) {
          // Vider le cache si rechargement forcé
          if (force) {
            Object.keys(translationCache).forEach(key => delete translationCache[key]);
          }
          
          // Construire le cache
          data.forEach((translation) => {
            if (!translationCache[translation.key]) {
              translationCache[translation.key] = {};
            }
            translationCache[translation.key][translation.language] = translation.value;
          });
          
          if (import.meta.env.DEV) {
            console.log(`[i18n] Loaded ${data.length} translations from database`);
            console.log(`[i18n] Cache now contains ${Object.keys(translationCache).length} unique keys`);
          }
        }
      } catch (error) {
        console.error('[i18n] Error loading translations from database:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [isLoading]);

  useEffect(() => {
    loadTranslations();
  }, [loadTranslations]);

  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  // Fonction pour recharger les traductions (utile pour l'admin)
  const reloadTranslations = useCallback(async () => {
    await loadTranslations(true);
  }, [loadTranslations]);

  // Statistiques sur les traductions
  const translationStats = {
    codeKeys: Object.keys(basicTranslations['fr'] || {}).length,
    dbKeys: Object.keys(translationCache).length,
    totalKeys: new Set([
      ...Object.keys(basicTranslations['fr'] || {}),
      ...Object.keys(translationCache)
    ]).size
  };

  const t = useCallback((key: string, fallback?: string): string => {
    // HYBRID TRANSLATION SYSTEM
    // Priority: Code (fast) → Database (dynamic) → Fallbacks → Key
    
    // 1) Built-in translations in current language (FASTEST - no DB query)
    const basicCurrent = basicTranslations[language]?.[key];
    if (basicCurrent) return basicCurrent;

    // 2) Database translation in current language (DYNAMIC content)
    const dbCurrent = translationCache[key]?.[language];
    if (dbCurrent) return dbCurrent;

    // 3) Fallback to built-in French (default language)
    const basicFr = basicTranslations['fr']?.[key];
    if (basicFr) return basicFr;

    // 4) Fallback to database French (legacy default)
    const dbFr = translationCache[key]?.['fr'];
    if (dbFr) return dbFr;

    // 5) Use provided fallback or return the key itself
    if (fallback) return fallback;
    
    // Log missing translations in development
    if (import.meta.env.DEV) {
      console.warn(`[i18n] Missing translation: "${key}" for language: ${language}`);
    }
    
    return key;
  }, [language]);

  const value = useMemo(() => ({
    language, 
    setLanguage, 
    t, 
    reloadTranslations,
    translationStats
  }), [language, t, reloadTranslations]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};